import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ProfaseModal from '../../../commons/ProfaseModal';
import axios, { isAxiosError } from 'axios';
import { FaArrowLeft } from '@react-icons/all-files/fa/FaArrowLeft';
import { FaCopy } from '@react-icons/all-files/fa/FaCopy';
import { FaExclamationTriangle } from '@react-icons/all-files/fa/FaExclamationTriangle';
import { FaDownload } from '@react-icons/all-files/fa/FaDownload';
import { FaFileImport } from '@react-icons/all-files/fa/FaFileImport';
import { FaPencilAlt } from '@react-icons/all-files/fa/FaPencilAlt';
import { FaPlus } from '@react-icons/all-files/fa/FaPlus';
import { FaSave } from '@react-icons/all-files/fa/FaSave';
import { FaTrash } from '@react-icons/all-files/fa/FaTrash';
import { TabPanelContainer, TabPanel } from '../../../commons/TabPanel';
import PageAlert from '../../../commons/PageAlert';
import { useAppDispatch, useAppSelector } from '../../../store';
import { getEmpresaConfigEnGrupo, getEmpresasByGrupo, importEmpresaConfigPdf, updateEmpresaConfigEnGrupo, EMPRESA_CONFIG_UPLOAD_FILE_KEY } from '../grupo-empresarial.reducer';
import {
  type EmpresaConfigApi,
  type ImportPdfFieldErrors,
  empresaConfigQuoContabilitaShowsStoredPassword,
  empresaConfigQuoFacturitaShowsStoredPassword,
  extractImportacionFieldErrors,
  extractImportWarningFromBody,
  fileHasPdfMagic,
  normalizeEmpresaConfigPayload,
  parseImportConfigBody,
  sanitizeEmpresaConfigQuoEchoPasswordsForClientState,
  str,
} from './empresa-config-import.shared';

export type { EmpresaConfigApi, ImportPdfFieldErrors } from './empresa-config-import.shared';

const ACCENT = '#6366f1';
/** Borde de sección (mismo azul, 70 % opacidad). */
const ACCENT_BORDER_70 = 'rgba(99, 102, 241, 0.7)';

const REPRESENTANTE_LEGAL_OTRO = 'OTRO';

function apiErrorMessage(error: unknown, fallback: string): string {
  if (error == null) return fallback;
  const err = error as { response?: { data?: unknown }; data?: unknown; message?: string };
  const candidates = [err.response?.data, err.data, error];
  for (const data of candidates) {
    if (data == null || typeof data !== 'object') continue;
    const body = data as { detail?: unknown; message?: unknown; title?: unknown };
    const detail = body.detail != null ? String(body.detail).trim() : '';
    if (detail) return detail;
    const message = body.message != null ? String(body.message).trim() : '';
    if (message) return message;
    const title = body.title != null ? String(body.title).trim() : '';
    if (title) return title;
  }
  if (typeof err.message === 'string' && err.message.trim()) return err.message.trim();
  return fallback;
}

/** Campos principales (sección 1) */
export interface EmpresaEditarFormValues {
  razonSocial: string;
  rfc: string;
}

export interface DomicilioFormValues {
  cp: string;
  tvialidad: string;
  ne: string;
  ni: string;
  localidad: string;
  ef: string;
  vialidad: string;
  colonia: string;
  municipio: string;
  entrec1: string;
  entrec2: string;
}

const inputStyle: React.CSSProperties = {
  padding: '5px 8px',
  border: '1px solid var(--crm-border)',
  borderRadius: 5,
  fontSize: 13,
  color: 'var(--crm-text-primary)',
  outline: 'none',
  boxSizing: 'border-box',
  width: '100%',
  background: 'var(--crm-input-bg)',
};

/** Inputs solo lectura (resúmenes QUO, etc.). */
const inputReadOnlyStyle: React.CSSProperties = {
  ...inputStyle,
  background: 'var(--crm-surface-muted)',
  color: 'var(--crm-text-secondary)',
  cursor: 'default',
};

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--crm-text-secondary)',
  userSelect: 'none',
};

const fieldGroupStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};

/** Fila etiqueta + campo (Info de Empresa) */
const infoEmpresaFieldRowStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: 8,
};

const infoEmpresaLabelStyle: React.CSSProperties = {
  ...labelStyle,
  flex: '0 0 104px',
  paddingTop: 5,
  lineHeight: 1.35,
};

const infoEmpresaControlColStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};

const representanteLegalRfcLabelStyle: React.CSSProperties = {
  ...infoEmpresaLabelStyle,
  flex: '0 0 36px',
};

const representanteLegalRazonSocialLabelStyle: React.CSSProperties = {
  ...infoEmpresaLabelStyle,
  flex: '0 0 92px',
};

/** Etiqueta en fila para domicilio (textos algo más largos que Info empresa). */
const domicilioLabelStyle: React.CSSProperties = {
  ...labelStyle,
  flex: '0 0 138px',
  paddingTop: 5,
  lineHeight: 1.35,
};

/** Etiqueta más estrecha cuando dos campos comparten fila dentro de una columna. */
const domicilioLabelStyleCompact: React.CSSProperties = {
  ...labelStyle,
  flex: '0 0 100px',
  paddingTop: 5,
  lineHeight: 1.35,
};

/** Barra del título vertical: redondeo solo a la izquierda; derecha a 90° (sin radio). */
const sectionVerticalTitleBarStyle: React.CSSProperties = {
  alignSelf: 'stretch',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: '0 0 auto',
  background: ACCENT,
  borderTopLeftRadius: 7,
  borderBottomLeftRadius: 7,
  borderTopRightRadius: 0,
  borderBottomRightRadius: 0,
  boxShadow: 'inset -1px 0 0 rgba(255,255,255,0.2)',
  padding: '10px 8px',
  boxSizing: 'border-box',
};

/** Texto del título (rotación aquí no afecta el border-radius de la barra). */
const sectionVerticalTitleTextStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 12,
  fontWeight: 700,
  color: '#fff',
  writingMode: 'vertical-rl',
  transform: 'rotate(180deg)',
  textOrientation: 'mixed',
  lineHeight: 1.2,
};

/** Marco de sección: borde con redondeo en las cuatro esquinas. */
const sectionWithVerticalTitleRowStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'stretch',
  gap: 0,
  width: '100%',
  minWidth: 0,
  border: `1px solid ${ACCENT_BORDER_70}`,
  borderRadius: 8,
  boxSizing: 'border-box',
  /** visible: el autocompletado de régimen usa position:absolute y quedaría recortado con hidden. */
  overflow: 'visible',
};

/** Contenido a la derecha del título. */
const sectionVerticalContentStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  padding: '10px 12px',
};

const sectionBoxStyle: React.CSSProperties = {
  marginBottom: 6,
};

/** Info de Empresa (ancho completo) encima; Régimen debajo. */
const infoRegimenStackStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  marginBottom: 6,
  width: '100%',
};

/** Dos columnas fijas para domicilio (orden SAT). */
const domicilioRegistroGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '8px 14px',
  alignItems: 'start',
};

const domicilioColumnStack: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 7,
};

/** Solo para pares explícitos (CP+TV, NE+NI, entrec1+entrec2). */
const domicilioTwinRowStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  gap: 8,
  alignItems: 'flex-start',
};

const domicilioTwinCellStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
};

const placeholderPanelStyle: React.CSSProperties = {
  padding: '10px 10px',
  fontSize: 12,
  color: 'var(--crm-text-secondary)',
  lineHeight: 1.45,
  background: 'var(--crm-surface-muted)',
  borderRadius: 6,
  border: '1px solid var(--crm-border)',
};

const actionsStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 8,
  paddingTop: 12,
  marginTop: 6,
  borderTop: '1px solid var(--crm-border)',
};

const btnSecondary: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  padding: '6px 14px',
  background: 'var(--crm-surface-muted)',
  color: 'var(--crm-text-secondary)',
  border: '1px solid var(--crm-border)',
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};

const btnPrimary = (saving: boolean): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  padding: '6px 14px',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 600,
  cursor: saving ? 'not-allowed' : 'pointer',
  background: ACCENT,
  opacity: saving ? 0.75 : 1,
});

/** Fecha API (`vsellos`, `vfiel`, `inicioO`, …) → yyyy-MM-dd para input type="date" (solo fecha). */
function vsellosToDateInput(v: unknown): string {
  if (v == null || v === '') return '';
  const s = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  return '';
}

/** `info.estatus` (0|1|2); si es null, inferir por `estatusT` (solo etiquetas en el combo). */
function estatusInfoToSelectValue(info: Record<string, any>): '' | '0' | '1' | '2' {
  const e = info.estatus;
  if (e === 0 || e === '0') return '0';
  if (e === 1 || e === '1') return '1';
  if (e === 2 || e === '2') return '2';
  const t = str(info.estatusT).toLowerCase();
  if (t.includes('incubado')) return '2';
  if (t.includes('inactivo')) return '0';
  if (t.includes('activo')) return '1';
  return '';
}

function representanteLegalIdFromInfo(info: Record<string, any>): string {
  const rep = info.representanteLegal;
  if (rep != null && typeof rep === 'object' && !Array.isArray(rep) && rep.id != null) {
    return String(rep.id);
  }
  if (info.representanteLegalId != null && info.representanteLegalId !== '') {
    return String(info.representanteLegalId);
  }
  return '';
}

function representanteLegalFromInfo(
  info: Record<string, any>,
  empresas: any[] | undefined,
): { selection: string; rfc: string; razonSocial: string } {
  const repId = representanteLegalIdFromInfo(info);
  const rfcFromInfo = str(info.representanteLegalRfc ?? info.representanteLegal?.rfc);
  const razonFromInfo = str(
    info.representanteLegalRazonSocial ?? info.representanteLegal?.razonSocial,
  );

  if (repId) {
    const emp = (empresas ?? []).find(e => String(e?.id) === repId);
    return {
      selection: repId,
      rfc: str(emp?.rfc) || rfcFromInfo,
      razonSocial: str(emp?.razonSocial) || razonFromInfo,
    };
  }

  if (rfcFromInfo || razonFromInfo) {
    return {
      selection: REPRESENTANTE_LEGAL_OTRO,
      rfc: rfcFromInfo,
      razonSocial: razonFromInfo,
    };
  }

  return { selection: '', rfc: '', razonSocial: '' };
}

function isEmpresaMoral(info: Record<string, any> | undefined): boolean {
  return info != null && info.empresaFisica === false;
}

/** Tabla de regímenes, actividades y archivos (estilos en .profase-empresa-edit-table). */

function regimenRowValor(row: any): string {
  const reg = row?.regimen;
  return str(reg?.valor ?? reg?.descripcion).trim() || '—';
}

function formatActividadPorcentaje(p: unknown): string {
  if (p == null || p === '') return '—';
  const n = Number(p);
  if (Number.isNaN(n)) return `${str(p)}%`;
  return `${n}%`;
}

function ActividadEconomicaTable({
  rows,
  loading,
  disabled,
  onEditRow,
  onRemoveRow,
}: {
  rows: any[];
  loading: boolean;
  disabled: boolean;
  onEditRow: (index: number) => void;
  onRemoveRow: (index: number) => void;
}) {
  if (loading) return <div style={placeholderPanelStyle}>Cargando…</div>;
  if (!rows.length) return <div style={placeholderPanelStyle}>Sin actividades económicas.</div>;
  return (
    <table className="profase-empresa-edit-table">
      <thead>
        <tr>
          <th style={{ width: 132 }}>Inicio de Actividad</th>
          <th style={{ width: 72, textAlign: 'right' }}>%</th>
          <th>Actividad Económica</th>
          <th style={{ width: 56, textAlign: 'center' }} title="Editar">
            Editar
          </th>
          <th style={{ width: 72, textAlign: 'center' }}>Eliminar</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, idx) => (
          <tr key={row?.id != null ? String(row.id) : `ae-${idx}`}>
            <td>{vsellosToDateInput(row.inicioA) || '—'}</td>
            <td style={{ textAlign: 'right' }}>{formatActividadPorcentaje(row.porcentaje)}</td>
            <td>{str(row.descripcionActividad).trim() || '—'}</td>
            <td style={{ textAlign: 'center' }}>
              <button
                type="button"
                className="profase-form-btn-tiny-accent"
                style={{
                  opacity: disabled ? 0.5 : 1,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                }}
                disabled={disabled}
                title="Editar"
                onClick={() => onEditRow(idx)}
              >
                <FaPencilAlt />
              </button>
            </td>
            <td style={{ textAlign: 'center' }}>
              <button
                type="button"
                className="profase-form-btn-tiny-danger"
                style={{
                  opacity: disabled ? 0.5 : 1,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                }}
                disabled={disabled}
                title="Quitar"
                onClick={() => onRemoveRow(idx)}
              >
                <FaTrash />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ObligacionesFiscalesTable({
  rows,
  loading,
  disabled,
  onEditRow,
  onRemoveRow,
}: {
  rows: any[];
  loading: boolean;
  disabled: boolean;
  onEditRow: (index: number) => void;
  onRemoveRow: (index: number) => void;
}) {
  if (loading) return <div style={placeholderPanelStyle}>Cargando…</div>;
  if (!rows.length) return <div style={placeholderPanelStyle}>Sin obligaciones fiscales.</div>;
  const cellMulti: React.CSSProperties = { whiteSpace: 'pre-wrap' as const };
  const cellVencimientoDesc: React.CSSProperties = { ...cellMulti, fontSize: 11 };
  return (
    <table className="profase-empresa-edit-table">
      <thead>
        <tr>
          <th>Descripción de la Obligación</th>
          <th style={{ fontSize: 11 }}>Descripción del Vencimiento</th>
          <th style={{ width: 120 }}>Fecha Inicio</th>
          <th style={{ width: 56, textAlign: 'center' }} title="Editar">
            Editar
          </th>
          <th style={{ width: 72, textAlign: 'center' }}>Eliminar</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, idx) => (
          <tr key={row?.id != null ? String(row.id) : `of-${idx}`}>
            <td style={cellMulti}>{str(row.descripcionObligacion).trim() || '—'}</td>
            <td style={cellVencimientoDesc}>{str(row.descripcionVencimiento).trim() || '—'}</td>
            <td>{vsellosToDateInput(row.fechaInicio) || '—'}</td>
            <td style={{ textAlign: 'center' }}>
              <button
                type="button"
                className="profase-form-btn-tiny-accent"
                style={{
                  opacity: disabled ? 0.5 : 1,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                }}
                disabled={disabled}
                title="Editar"
                onClick={() => onEditRow(idx)}
              >
                <FaPencilAlt />
              </button>
            </td>
            <td style={{ textAlign: 'center' }}>
              <button
                type="button"
                className="profase-form-btn-tiny-danger"
                style={{
                  opacity: disabled ? 0.5 : 1,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                }}
                disabled={disabled}
                title="Quitar"
                onClick={() => onRemoveRow(idx)}
              >
                <FaTrash />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const masInfoFormFooterStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 8,
  marginTop: 14,
  paddingTop: 12,
  borderTop: '1px solid var(--crm-border)',
};

const EMPRESA_PASSWORD_COPY_DELAY_MS = 2000;

const empresaPwdControlRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 6,
  alignItems: 'center',
  width: '100%',
  minWidth: 0,
};

const empresaMainFieldCopyRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 6,
  alignItems: 'center',
  width: '100%',
  minWidth: 0,
};

/** Copiar al portapapeles (RFC, razón social). */
function EmpresaTextCopyIconButton({
  text,
  ariaLabel,
  disabled,
}: {
  text: string;
  ariaLabel: string;
  disabled?: boolean;
}) {
  const canCopy = Boolean(!disabled && typeof document !== 'undefined' && text.trim().length > 0);
  return (
    <button
      type="button"
      disabled={!canCopy}
      title={ariaLabel}
      aria-label={ariaLabel}
      onClick={() => void copyPlainTextToClipboardRobust(text)}
      className="profase-form-copy-btn"
    >
      <FaCopy aria-hidden />
    </button>
  );
}


/** GET …/key/* — valor en Base64; segmentos conocidos → prioridad en campos JSON. */
function pickPasswordFromQuoKeyResponse(
  data: unknown,
  kind?: 'contabilita' | 'facturita' | 'claveFiel' | 'claveCiec',
): string {
  if (data == null) return '';
  if (typeof data === 'string') return data.trim();
  if (typeof data !== 'object') return '';
  const o = data as Record<string, unknown>;

  const kindFirst =
    kind === 'facturita'
      ? ['facturita', 'keyFacturita', 'claveFacturita', 'pqf', 'passwordFacturita']
      : kind === 'contabilita'
        ? ['contabilita', 'keyContabilita', 'claveContabilita', 'pqc', 'passwordContabilita']
        : kind === 'claveFiel'
          ? ['claveFiel', 'clave_fiel', 'claveFIEL', 'cfiel', 'claveFIELB64', 'fielKey', 'fiel']
          : kind === 'claveCiec'
            ? ['ciec', 'CIEC', 'claveCiec', 'claveCIEC', 'clave_ciec', 'csellos', 'cveSellos', 'claveSellos', 'valorCiec']
            : [];

  const generic = [
    'password',
    'contrasena',
    'contraseña',
    'valor',
    'value',
    'clave',
    'secret',
    'llave',
    'key',
    'dato',
    'texto',
    'encoded',
    'base64',
    'credencial',
    'contenido',
    'pqc',
    'pqf',
  ];

  const seen = new Set<string>();
  for (const k of [...kindFirst, ...generic]) {
    if (seen.has(k)) continue;
    seen.add(k);
    const v = o[k];
    if (typeof v === 'string' && v.trim() !== '') return v.trim();
  }

  const inner = o.data ?? o.payload ?? o.body;
  if (inner != null && inner !== o) return pickPasswordFromQuoKeyResponse(inner, kind);

  let lone = '';
  let loneCount = 0;
  for (const v of Object.values(o)) {
    if (typeof v === 'string' && v.trim() !== '') {
      loneCount += 1;
      lone = v.trim();
    }
  }
  if (loneCount === 1) return lone;

  return '';
}

/**
 * El backend envía la clave en Base64 (UTF-8). Si no es Base64 válido, se devuelve el texto original.
 * Úsese tras GET …/key/* (QUO, `clave-fiel`, etc.) cuando el cuerpo viene en Base64.
 */
function decodeQuoKeyPasswordFromBase64(encoded: string): string {
  const t = encoded.replace(/\s/g, '').trim();
  if (!t) return '';
  try {
    const binary = atob(t);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    const decoded = new TextDecoder('utf-8', { fatal: false }).decode(bytes);
    return decoded.replace(/\0+$/, '').trim();
  } catch {
    return encoded.trim();
  }
}

/** Clipboard API suele fallar sin HTTPS/Gestión de portapapeles; fallback DOM. */
async function copyPlainTextToClipboardRobust(text: string): Promise<boolean> {
  const t = text.trim();
  if (!t) return false;
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(t);
      return true;
    } catch {
      /* seguir */
    }
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = t;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '-1000px';
    ta.style.left = '0';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

function canFetchEmpresaQuoKeyPassword(
  grupoId: string | number | null | undefined,
  empresaId: string | number | null | undefined,
): boolean {
  if (grupoId == null || empresaId == null) return false;
  const gs = String(grupoId).trim();
  const es = String(empresaId).trim();
  if (!gs || !es || gs === 'new' || es === 'new') return false;
  return true;
}

/** GET …/empresa/:id/key/:segment — secreto Base64 decodificado. */
async function fetchEmpresaDecodedKeySecret(params: {
  grupoId: string | number;
  empresaId: string | number;
  keyPathSegment: string;
  pickKind?: 'contabilita' | 'facturita' | 'claveFiel' | 'claveCiec';
}): Promise<string> {
  const { grupoId, empresaId, keyPathSegment, pickKind } = params;
  const url = `/api/grupo-empresarials/${grupoId}/empresa/${empresaId}/key/${keyPathSegment}`;
  const res = await axios.get<unknown>(url);
  const picked = pickPasswordFromQuoKeyResponse(res.data, pickKind);
  return decodeQuoKeyPasswordFromBase64(picked);
}

async function fetchEmpresaQuoKeyPassword(params: {
  grupoId: string | number;
  empresaId: string | number;
  kind: 'contabilita' | 'facturita';
}): Promise<string> {
  const { grupoId, empresaId, kind } = params;
  return fetchEmpresaDecodedKeySecret({ grupoId, empresaId, keyPathSegment: kind, pickKind: kind });
}

/** Copiar clave obteniendo GET …/key/:segment (Base64), fallback al valor local. Usa Clipboard + execCommand como respaldo. */
function EmpresaDecodedKeySecretCopyIconButton({
  grupoId,
  empresaId,
  localFallback,
  disabled,
  keyPathSegment,
  pickKind,
  ariaLabel,
  titleWithApi,
  titleLocalOnly,
}: {
  grupoId?: string | number | null;
  empresaId?: string | number | null;
  localFallback: string;
  disabled?: boolean;
  keyPathSegment: string;
  pickKind?: 'contabilita' | 'facturita' | 'claveFiel' | 'claveCiec';
  ariaLabel: string;
  titleWithApi: string;
  titleLocalOnly: string;
}) {
  const [copying, setCopying] = useState(false);
  const docOk = typeof document !== 'undefined';
  const useApi = Boolean(canFetchEmpresaQuoKeyPassword(grupoId, empresaId));
  const hasLocal = localFallback.trim().length > 0;
  const canAttempt = Boolean(!disabled && docOk && !copying && (useApi || hasLocal));

  const onClick = () => {
    if (!docOk || copying) return;
    if (!useApi && !hasLocal) return;
    setCopying(true);
    void (async () => {
      try {
        let text = '';
        if (useApi && grupoId != null && empresaId != null) {
          try {
            text = await fetchEmpresaDecodedKeySecret({
              grupoId,
              empresaId,
              keyPathSegment,
              pickKind,
            });
          } catch {
            text = '';
          }
        }
        if (!text.trim()) text = localFallback.trim();
        if (!text.trim()) return;
        await copyPlainTextToClipboardRobust(text);
      } finally {
        setCopying(false);
      }
    })();
  };

  return (
    <button
      type="button"
      disabled={!canAttempt}
      title={useApi ? titleWithApi : titleLocalOnly}
      aria-label={ariaLabel}
      onClick={onClick}
      className="profase-form-copy-btn"
    >
      <FaCopy aria-hidden />
    </button>
  );
}

/** @deprecated usar EmpresaDecodedKeySecretCopyIconButton */
function EmpresaClaveFielCopyIconButton({
  grupoId,
  empresaId,
  localFallback,
  disabled,
}: {
  grupoId?: string | number | null;
  empresaId?: string | number | null;
  localFallback: string;
  disabled?: boolean;
}) {
  return (
    <EmpresaDecodedKeySecretCopyIconButton
      grupoId={grupoId}
      empresaId={empresaId}
      localFallback={localFallback}
      disabled={disabled}
      keyPathSegment="clave-fiel"
      pickKind="claveFiel"
      ariaLabel="Copiar clave FIEL"
      titleWithApi="Obtiene la clave FIEL del servidor (GET …/key/clave-fiel, Base64 decodificado) y copia al portapapeles"
      titleLocalOnly="Copiar clave FIEL del campo"
    />
  );
}

function EmpresaPwdCopyBlock(props: {
  /** Contraseña en cliente (borrador o memoria); se usa si el GET no devuelve valor. */
  localPassword?: string;
  disabled?: boolean;
  ariaLabel?: string;
  children: React.ReactElement<{ style?: React.CSSProperties }>;
  /** Si hay ids válidos, primero se consulta GET …/key/:kind. */
  passwordKey?: 'contabilita' | 'facturita';
  grupoId?: string | number | null;
  empresaId?: string | number | null;
}) {
  const {
    localPassword = '',
    disabled,
    ariaLabel = 'Copiar contraseña',
    children,
    passwordKey,
    grupoId,
    empresaId,
  } = props;
  const [feedback, setFeedback] = useState<string | null>(null);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [copying, setCopying] = useState(false);
  const hideTimerRef = useRef<number | null>(null);
  const errHideTimerRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (hideTimerRef.current != null) window.clearTimeout(hideTimerRef.current);
      if (errHideTimerRef.current != null) window.clearTimeout(errHideTimerRef.current);
    },
    [],
  );

  const useApi = Boolean(passwordKey && canFetchEmpresaQuoKeyPassword(grupoId, empresaId));
  const hasLocal = localPassword.trim().length > 0;
  const hasClipboard = typeof navigator !== 'undefined' && Boolean(navigator.clipboard?.writeText);
  const canCopy = !disabled && hasClipboard && !copying && (useApi || hasLocal);

  const onCopy = () => {
    if (!hasClipboard || copying) return;
    if (!useApi && !hasLocal) return;
    setCopying(true);
    setFeedback(null);
    setFeedbackError(null);
    window.setTimeout(() => {
      void (async () => {
        try {
          let text = '';
          if (useApi && passwordKey && grupoId != null && empresaId != null) {
            try {
              text = await fetchEmpresaQuoKeyPassword({ grupoId, empresaId, kind: passwordKey });
            } catch {
              text = '';
            }
          }
          if (!text.trim()) text = localPassword.trim();
          if (!text) {
            setFeedbackError('No se pudo obtener la contraseña');
            if (errHideTimerRef.current != null) window.clearTimeout(errHideTimerRef.current);
            errHideTimerRef.current = window.setTimeout(() => setFeedbackError(null), 5000);
            return;
          }
          try {
            await navigator.clipboard.writeText(text);
          } catch {
            setFeedbackError('No se pudo copiar al portapapeles');
            if (errHideTimerRef.current != null) window.clearTimeout(errHideTimerRef.current);
            errHideTimerRef.current = window.setTimeout(() => setFeedbackError(null), 5000);
            return;
          }
          setFeedbackError(null);
          setFeedback('Copiado al portapapeles');
          if (hideTimerRef.current != null) window.clearTimeout(hideTimerRef.current);
          hideTimerRef.current = window.setTimeout(() => setFeedback(null), 4500);
        } finally {
          setCopying(false);
        }
      })();
    }, EMPRESA_PASSWORD_COPY_DELAY_MS);
  };

  const childStyle: React.CSSProperties = { ...(children.props.style ?? {}), flex: 1, minWidth: 0 };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%', minWidth: 0 }}>
      <div style={empresaPwdControlRowStyle}>
        {React.cloneElement(children, { style: childStyle })}
        <button
          type="button"
          disabled={!canCopy}
          title={
            useApi
              ? 'Esperará 2 s, obtendrá la contraseña del servidor y la copiará al portapapeles'
              : 'Esperará 2 s y copiará la contraseña al portapapeles'
          }
          aria-label={ariaLabel}
          onClick={onCopy}
          className="profase-form-copy-btn"
        >
          <FaCopy aria-hidden />
        </button>
      </div>
      {feedback ? (
        <span role="status" aria-live="polite" className="profase-form-feedback-success">
          {feedback}
        </span>
      ) : null}
      {feedbackError ? (
        <span role="alert" className="profase-form-feedback-error">
          {feedbackError}
        </span>
      ) : null}
    </div>
  );
}

const textareaMasInfoStyle: React.CSSProperties = {
  ...inputStyle,
  minHeight: 80,
  resize: 'vertical' as const,
  fontFamily: 'inherit',
};

function MasInfoActividadForm({
  disabled,
  draft,
  setDraft,
  titulo,
  onCancel,
  onAccept,
}: {
  disabled: boolean;
  draft: { inicioA: string; porcentaje: string; descripcionActividad: string };
  setDraft: React.Dispatch<
    React.SetStateAction<{ inicioA: string; porcentaje: string; descripcionActividad: string }>
  >;
  titulo?: string;
  onCancel: () => void;
  onAccept: () => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {titulo ? (
        <p className="profase-form-section-title" style={{ fontSize: 13 }}>{titulo}</p>
      ) : null}
      <div style={infoEmpresaFieldRowStyle}>
        <label style={infoEmpresaLabelStyle} htmlFor="masinfo-ae-inicio">
          Inicio de Actividad
        </label>
        <div style={infoEmpresaControlColStyle}>
          <input
            id="masinfo-ae-inicio"
            type="date"
            style={inputStyle}
            value={draft.inicioA}
            disabled={disabled}
            onChange={e => setDraft(p => ({ ...p, inicioA: e.target.value }))}
          />
        </div>
      </div>
      <div style={infoEmpresaFieldRowStyle}>
        <label style={infoEmpresaLabelStyle} htmlFor="masinfo-ae-pct">
          %
        </label>
        <div style={infoEmpresaControlColStyle}>
          <input
            id="masinfo-ae-pct"
            type="text"
            inputMode="decimal"
            style={inputStyle}
            value={draft.porcentaje}
            disabled={disabled}
            placeholder="Ej. 50"
            onChange={e => setDraft(p => ({ ...p, porcentaje: e.target.value }))}
          />
        </div>
      </div>
      <div style={infoEmpresaFieldRowStyle}>
        <label style={infoEmpresaLabelStyle} htmlFor="masinfo-ae-desc">
          Actividad Económica
        </label>
        <div style={infoEmpresaControlColStyle}>
          <textarea
            id="masinfo-ae-desc"
            style={textareaMasInfoStyle}
            value={draft.descripcionActividad}
            disabled={disabled}
            placeholder="Descripción"
            rows={3}
            onChange={e => setDraft(p => ({ ...p, descripcionActividad: e.target.value }))}
          />
        </div>
      </div>
      <div style={masInfoFormFooterStyle}>
        <button type="button" style={btnSecondary} disabled={disabled} onClick={onCancel}>
          Cancelar
        </button>
        <button type="button" style={btnPrimary(disabled)} disabled={disabled} onClick={onAccept}>
          Aceptar
        </button>
      </div>
    </div>
  );
}

function MasInfoObligacionForm({
  disabled,
  draft,
  setDraft,
  titulo,
  onCancel,
  onAccept,
}: {
  disabled: boolean;
  draft: { descripcionObligacion: string; descripcionVencimiento: string; fechaInicio: string };
  setDraft: React.Dispatch<
    React.SetStateAction<{
      descripcionObligacion: string;
      descripcionVencimiento: string;
      fechaInicio: string;
    }>
  >;
  titulo?: string;
  onCancel: () => void;
  onAccept: () => void;
}) {
  const descVencStyle: React.CSSProperties = { ...textareaMasInfoStyle, fontSize: 11 };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {titulo ? (
        <p className="profase-form-section-title" style={{ fontSize: 13 }}>{titulo}</p>
      ) : null}
      <div style={infoEmpresaFieldRowStyle}>
        <label style={infoEmpresaLabelStyle} htmlFor="masinfo-of-ob">
          Descripción de la Obligación
        </label>
        <div style={infoEmpresaControlColStyle}>
          <textarea
            id="masinfo-of-ob"
            style={textareaMasInfoStyle}
            value={draft.descripcionObligacion}
            disabled={disabled}
            rows={3}
            onChange={e => setDraft(p => ({ ...p, descripcionObligacion: e.target.value }))}
          />
        </div>
      </div>
      <div style={infoEmpresaFieldRowStyle}>
        <label style={{ ...infoEmpresaLabelStyle, fontSize: 11 }} htmlFor="masinfo-of-ven">
          Descripción del Vencimiento
        </label>
        <div style={infoEmpresaControlColStyle}>
          <textarea
            id="masinfo-of-ven"
            style={descVencStyle}
            value={draft.descripcionVencimiento}
            disabled={disabled}
            rows={3}
            onChange={e => setDraft(p => ({ ...p, descripcionVencimiento: e.target.value }))}
          />
        </div>
      </div>
      <div style={infoEmpresaFieldRowStyle}>
        <label style={infoEmpresaLabelStyle} htmlFor="masinfo-of-fi">
          Fecha Inicio
        </label>
        <div style={infoEmpresaControlColStyle}>
          <input
            id="masinfo-of-fi"
            type="date"
            style={inputStyle}
            value={draft.fechaInicio}
            disabled={disabled}
            onChange={e => setDraft(p => ({ ...p, fechaInicio: e.target.value }))}
          />
        </div>
      </div>
      <div style={masInfoFormFooterStyle}>
        <button type="button" style={btnSecondary} disabled={disabled} onClick={onCancel}>
          Cancelar
        </button>
        <button type="button" style={btnPrimary(disabled)} disabled={disabled} onClick={onAccept}>
          Aceptar
        </button>
      </div>
    </div>
  );
}

type MasInfoArchivoAltaDraft = {
  nombreArchivo: string;
  file: File | null;
  /** Opción elegida del catálogo REST (SAT / Sellos); vacío hasta elegir. */
  tipoId?: string;
};

/** Ítem de catálogo `GET api/archivo-sats` (SAT y Sellos comparten el mismo catálogo). */
type ArchivoTipoCatalogOption = {
  id: number;
  descripcion: string;
  createdBy?: unknown;
  created?: unknown;
  updatedBy?: unknown;
  updated?: unknown;
};

function archivoTipoNestedFromCatalogOpt(opt: ArchivoTipoCatalogOption): Record<string, unknown> {
  const descripcion = str(opt.descripcion).trim() || '—';
  const o: Record<string, unknown> = { id: opt.id, descripcion };
  if (opt.createdBy !== undefined && opt.createdBy !== null) o.createdBy = opt.createdBy;
  if (opt.created !== undefined && opt.created !== null) o.created = opt.created;
  if (opt.updatedBy !== undefined && opt.updatedBy !== null) o.updatedBy = opt.updatedBy;
  if (opt.updated !== undefined && opt.updated !== null) o.updated = opt.updated;
  return o;
}

function archivoTipoCatalogOptionsFromApiData(data: unknown): ArchivoTipoCatalogOption[] {
  const list = Array.isArray(data) ? data : [];
  return list
    .filter((x): x is Record<string, unknown> => x != null && typeof x === 'object')
    .map(row => {
      const idRaw = row.id;
      const descripcion = str(row.descripcion).trim();
      if (idRaw == null || idRaw === '') return null;
      const num = Number(idRaw);
      if (!Number.isFinite(num)) return null;
      const opt: ArchivoTipoCatalogOption = { id: num, descripcion: descripcion || '—' };
      if (row.createdBy !== undefined && row.createdBy !== null) opt.createdBy = row.createdBy;
      if (row.created !== undefined && row.created !== null) opt.created = row.created;
      if (row.updatedBy !== undefined && row.updatedBy !== null) opt.updatedBy = row.updatedBy;
      if (row.updated !== undefined && row.updated !== null) opt.updated = row.updated;
      return opt;
    })
    .filter((x): x is ArchivoTipoCatalogOption => x != null);
}

function MasInfoArchivoAltaForm({
  disabled,
  draft,
  setDraft,
  onCancel,
  onAccept,
  titulo,
  idPrefix,
  tipoCatalogSelect,
}: {
  disabled: boolean;
  draft: MasInfoArchivoAltaDraft;
  setDraft: React.Dispatch<React.SetStateAction<MasInfoArchivoAltaDraft>>;
  onCancel: () => void;
  onAccept: () => void;
  titulo: string;
  idPrefix: string;
  tipoCatalogSelect?: {
    label: string;
    options: ArchivoTipoCatalogOption[];
    loading: boolean;
    error: string | null;
  };
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const tipoId =
    draft.tipoId !== undefined ? draft.tipoId : '';
  const tipoSelectId = `masinfo-ar-tipo-${idPrefix}`;
  const nameId = `masinfo-ar-nombre-${idPrefix}`;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <p className="profase-form-section-title" style={{ fontSize: 12 }}>{titulo}</p>
      {tipoCatalogSelect ? (
        <div style={infoEmpresaFieldRowStyle}>
          <label style={infoEmpresaLabelStyle} htmlFor={tipoSelectId}>
            {tipoCatalogSelect.label}
          </label>
          <div style={infoEmpresaControlColStyle}>
            <select
              id={tipoSelectId}
              style={inputStyle}
              disabled={disabled || tipoCatalogSelect.loading}
              value={tipoId}
              onChange={e => setDraft(p => ({ ...p, tipoId: e.target.value }))}
            >
              <option value="">
                {tipoCatalogSelect.loading ? 'Cargando…' : 'Seleccione tipo'}
              </option>
              {tipoCatalogSelect.options.map(opt => (
                <option key={opt.id} value={String(opt.id)}>
                  {str(opt.descripcion).trim() || `— (${opt.id})`}
                </option>
              ))}
            </select>
            {tipoCatalogSelect.error ? (
              <span style={{ fontSize: 11, color: '#b91c1c' }}>{tipoCatalogSelect.error}</span>
            ) : null}
          </div>
        </div>
      ) : null}
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        aria-hidden
        tabIndex={-1}
        onChange={e => {
          const f = e.target.files?.[0];
          if (f) setDraft(p => ({ ...p, file: f, nombreArchivo: f.name }));
          e.target.value = '';
        }}
      />
      <div style={infoEmpresaFieldRowStyle}>
        <label style={infoEmpresaLabelStyle} id={`${nameId}-file-label`}>
          Archivo
        </label>
        <div style={infoEmpresaControlColStyle}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
            <button
              type="button"
              style={{ ...btnPrimary(disabled), alignSelf: 'flex-start' }}
              disabled={disabled}
              aria-labelledby={`${nameId}-file-label`}
              onClick={() => fileInputRef.current?.click()}
            >
              <FaFileImport style={{ marginRight: 6 }} aria-hidden />
              Seleccionar Archivo
            </button>
            <span className="profase-form-muted-text" style={{ fontSize: 11, lineHeight: 1.4 }}>
              {draft.file
                ? `Archivo seleccionado: ${draft.file.name}`
                : 'Seleccione primero un archivo con el botón.'}
            </span>
          </div>
        </div>
      </div>
      <div style={infoEmpresaFieldRowStyle}>
        <label style={infoEmpresaLabelStyle} htmlFor={nameId}>
          Nombre de archivo
        </label>
        <div style={infoEmpresaControlColStyle}>
          <input
            id={nameId}
            type="text"
            style={inputStyle}
            value={draft.nombreArchivo}
            disabled={disabled}
            placeholder="Nombre del archivo"
            onChange={e => setDraft(p => ({ ...p, nombreArchivo: e.target.value }))}
          />
        </div>
      </div>
      <div style={masInfoFormFooterStyle}>
        <button type="button" style={btnSecondary} disabled={disabled} onClick={onCancel}>
          Cancelar
        </button>
        <button type="button" style={btnPrimary(disabled)} disabled={disabled} onClick={onAccept}>
          Aceptar
        </button>
      </div>
    </div>
  );
}

function unwrapKeyedObject(u: unknown): Record<string, unknown> | null {
  if (u == null) return null;
  if (Array.isArray(u) && u.length > 0 && u[0] != null && typeof u[0] === 'object' && !Array.isArray(u[0])) {
    return u[0] as Record<string, unknown>;
  }
  if (typeof u === 'object' && !Array.isArray(u)) {
    return u as Record<string, unknown>;
  }
  return null;
}

/** Primer valor no vacío buscando `keys` en cada objeto de `sources` (orden = prioridad de lectura). */
function pickScalarFromSources(sources: (Record<string, unknown> | null | undefined)[], keys: string[]): string {
  for (const obj of sources) {
    if (!obj) continue;
    for (const k of keys) {
      const v = obj[k];
      if (v != null && String(v).trim() !== '') return String(v).trim();
    }
  }
  return '';
}

type QuoContabilitaFormValues = {
  quoId: string;
  usuario: string;
  contrasena: string;
};

/** Une `usuariosQUO`, `quoContabilita` e `info` (idquo, uqc, pqc). */
function normalizeQuoContabilitaDraftFromEmpresaConfig(ec: EmpresaConfigApi | null): QuoContabilitaFormValues {
  if (!ec) return { quoId: '', usuario: '', contrasena: '' };
  const uqo = unwrapKeyedObject(ec.usuariosQUO);
  const qc = unwrapKeyedObject(ec.quoContabilita);
  const info = ec.info && typeof ec.info === 'object' ? (ec.info as Record<string, unknown>) : null;
  const quoId = pickScalarFromSources([uqo, qc, info], ['quoId', 'QUOId', 'idquo', 'id']);
  const usuario = pickScalarFromSources([uqo, qc, info], ['usuario', 'Usuario', 'uqc']);
  const contrasena = pickScalarFromSources([uqo, qc, info], ['contrasena', 'contraseña', 'Contraseña', 'password', 'pqc']);
  return { quoId, usuario, contrasena };
}

type QuoFacturitaFormValues = {
  usuario: string;
  contrasena: string;
};

/** Une `usuariosQUOFacturita`, `quoFacturita` e `info` (uqf, pqf). */
function normalizeQuoFacturitaDraftFromEmpresaConfig(ec: EmpresaConfigApi | null): QuoFacturitaFormValues {
  if (!ec) return { usuario: '', contrasena: '' };
  const uf = unwrapKeyedObject(ec.usuariosQUOFacturita);
  const qf = unwrapKeyedObject(ec.quoFacturita);
  const info = ec.info && typeof ec.info === 'object' ? (ec.info as Record<string, unknown>) : null;
  return {
    usuario: pickScalarFromSources([uf, qf, info], ['usuario', 'Usuario', 'uqf']),
    contrasena: pickScalarFromSources([uf, qf, info], ['contrasena', 'contraseña', 'Contraseña', 'password', 'pqf']),
  };
}

/** Conserva otros campos de `usuariosQUO`; escribe también idquo/uqc/pqc para compatibilidad con API. */
function usuariosQUOAfterContabilitaEdit(
  prevUsuariosQUO: unknown,
  draft: QuoContabilitaFormValues,
  contrasenaModificada: boolean,
): Record<string, unknown> {
  let base: Record<string, unknown> = {};
  if (
    Array.isArray(prevUsuariosQUO) &&
    prevUsuariosQUO.length > 0 &&
    prevUsuariosQUO[0] != null &&
    typeof prevUsuariosQUO[0] === 'object'
  ) {
    base = { ...(prevUsuariosQUO[0] as Record<string, unknown>) };
  } else if (prevUsuariosQUO != null && typeof prevUsuariosQUO === 'object' && !Array.isArray(prevUsuariosQUO)) {
    base = { ...(prevUsuariosQUO as Record<string, unknown>) };
  }
  const qidTrim = draft.quoId.trim();
  const usuario = draft.usuario.trim();
  const contrasena = draft.contrasena.trim();
  const idParsed =
    qidTrim === '' ? null : Number.isFinite(Number(qidTrim)) && !Number.isNaN(Number(qidTrim)) ? Number(qidTrim) : qidTrim;

  base.quoId = qidTrim === '' ? null : qidTrim;
  base.usuario = usuario === '' ? null : usuario;
  base.idquo = idParsed;
  base.uqc = usuario === '' ? null : usuario;
  if (contrasenaModificada) {
    base.contrasena = contrasena === '' ? null : contrasena;
    base.pqc = contrasena === '' ? null : contrasena;
  } else {
    delete base.contrasena;
    delete base.pqc;
  }

  return base;
}

/** Conserva otros campos de `usuariosQUOFacturita`; escribe también uqf/pqf para compatibilidad con API. */
function usuariosQUOFacturitaAfterFacturitaEdit(
  prevUsuariosQUOFacturita: unknown,
  draft: QuoFacturitaFormValues,
  contrasenaModificada: boolean,
): Record<string, unknown> {
  let base: Record<string, unknown> = {};
  if (Array.isArray(prevUsuariosQUOFacturita) && prevUsuariosQUOFacturita.length > 0 && prevUsuariosQUOFacturita[0] != null && typeof prevUsuariosQUOFacturita[0] === 'object') {
    base = { ...(prevUsuariosQUOFacturita[0] as Record<string, unknown>) };
  } else if (prevUsuariosQUOFacturita != null && typeof prevUsuariosQUOFacturita === 'object' && !Array.isArray(prevUsuariosQUOFacturita)) {
    base = { ...(prevUsuariosQUOFacturita as Record<string, unknown>) };
  }
  const usuario = draft.usuario.trim();
  const contrasena = draft.contrasena.trim();
  base.usuario = usuario === '' ? null : usuario;
  base.uqf = usuario === '' ? null : usuario;
  if (contrasenaModificada) {
    base.contrasena = contrasena === '' ? null : contrasena;
    base.pqf = contrasena === '' ? null : contrasena;
  } else {
    delete base.contrasena;
    delete base.pqf;
  }
  return base;
}

function mergeQuoContabilitaIntoEmpresaConfig(
  prev: EmpresaConfigApi,
  draft: QuoContabilitaFormValues,
  contrasenaModificada: boolean,
): EmpresaConfigApi {
  const nextU = usuariosQUOAfterContabilitaEdit(prev.usuariosQUO, draft, contrasenaModificada);
  const qidTrim = draft.quoId.trim();
  const usuario = draft.usuario.trim();
  const contrasenaTrim = draft.contrasena.trim();
  const idParsed =
    qidTrim === '' ? null : Number.isFinite(Number(qidTrim)) && !Number.isNaN(Number(qidTrim)) ? Number(qidTrim) : qidTrim;

  const prevQcRaw =
    typeof prev.quoContabilita === 'object' && prev.quoContabilita != null && !Array.isArray(prev.quoContabilita)
      ? (prev.quoContabilita as Record<string, unknown>)
      : {};
  const {
    contrasena: _prevQco,
    pqc: _prevQcp,
    ...prevQc
  } = prevQcRaw;

  const quoContabilita = {
    ...prevQc,
    id: idParsed,
    usuario,
    ...(contrasenaModificada
      ? { contrasena: contrasenaTrim === '' ? null : contrasenaTrim }
      : {}),
  };

  const prevInfo = (prev.info ?? {}) as Record<string, unknown>;
  const { pqc: _omitPqc, ...infoSinPqc } = prevInfo;
  const info = {
    ...infoSinPqc,
    idquo: idParsed,
    uqc: usuario === '' ? null : usuario,
    ...(contrasenaModificada ? { pqc: contrasenaTrim === '' ? null : contrasenaTrim } : {}),
  };

  return {
    ...prev,
    usuariosQUO: nextU as any,
    quoContabilita,
    info,
  };
}

function mergeQuoFacturitaIntoEmpresaConfig(
  prev: EmpresaConfigApi,
  draft: QuoFacturitaFormValues,
  contrasenaModificada: boolean,
): EmpresaConfigApi {
  const nextU = usuariosQUOFacturitaAfterFacturitaEdit(prev.usuariosQUOFacturita, draft, contrasenaModificada);
  const usuario = draft.usuario.trim();
  const contrasenaTrim = draft.contrasena.trim();
  const prevQfRaw =
    typeof prev.quoFacturita === 'object' && prev.quoFacturita != null && !Array.isArray(prev.quoFacturita)
      ? (prev.quoFacturita as Record<string, unknown>)
      : {};
  const {
    contrasena: _prevQfo,
    pqf: _prevQfp,
    ...prevQf
  } = prevQfRaw;
  const quoFacturita = {
    ...prevQf,
    usuario,
    ...(contrasenaModificada
      ? { contrasena: contrasenaTrim === '' ? null : contrasenaTrim }
      : {}),
  };
  const prevInfoF = (prev.info ?? {}) as Record<string, unknown>;
  const { pqf: _omitPqf, ...infoSinPqf } = prevInfoF;
  const info = {
    ...infoSinPqf,
    uqf: usuario === '' ? null : usuario,
    ...(contrasenaModificada ? { pqf: contrasenaTrim === '' ? null : contrasenaTrim } : {}),
  };
  return {
    ...prev,
    usuariosQUOFacturita: nextU as any,
    quoFacturita,
    info,
  };
}

function QuoContabilitaResumen({
  config,
  passwordKnownOnServer,
  grupoId,
  empresaId,
}: {
  config: EmpresaConfigApi | null | undefined;
  /** True si el servidor tiene clave (GET solo enmascara; el estado local ya no guarda ese eco). */
  passwordKnownOnServer?: boolean;
  grupoId?: string | number | null;
  empresaId?: string | number | null;
}) {
  const d = normalizeQuoContabilitaDraftFromEmpresaConfig(config ?? null);
  const showPw = Boolean(d.contrasena) || Boolean(passwordKnownOnServer);
  const hasAny = Boolean(d.quoId || d.usuario || showPw);
  if (!hasAny) return <div style={placeholderPanelStyle}>Sin datos QUO Contabilita.</div>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={infoEmpresaFieldRowStyle}>
        <span style={infoEmpresaLabelStyle}>QUOId</span>
        <div style={infoEmpresaControlColStyle}>
          <input type="text" readOnly style={inputReadOnlyStyle} value={d.quoId || ''} placeholder="—" aria-readonly />
        </div>
      </div>
      <div style={infoEmpresaFieldRowStyle}>
        <span style={infoEmpresaLabelStyle}>Usuario</span>
        <div style={infoEmpresaControlColStyle}>
          <input
            type="text"
            readOnly
            style={inputReadOnlyStyle}
            value={d.usuario || ''}
            placeholder="—"
            aria-readonly
            autoComplete="off"
            spellCheck={false}
          />
        </div>
      </div>
      <div style={infoEmpresaFieldRowStyle}>
        <span style={infoEmpresaLabelStyle}>Contraseña</span>
        <div style={infoEmpresaControlColStyle}>
          <EmpresaPwdCopyBlock
            localPassword={d.contrasena}
            passwordKey="contabilita"
            grupoId={grupoId}
            empresaId={empresaId}
            ariaLabel="Copiar contraseña QUO Contabilita"
          >
            <input
              type="password"
              readOnly
              style={inputReadOnlyStyle}
              value={showPw ? '********' : ''}
              placeholder={showPw ? undefined : '—'}
              aria-readonly
              autoComplete="off"
              spellCheck={false}
            />
          </EmpresaPwdCopyBlock>
        </div>
      </div>
    </div>
  );
}

function QuoFacturitaResumen({
  config,
  passwordKnownOnServer,
  grupoId,
  empresaId,
}: {
  config: EmpresaConfigApi | null | undefined;
  passwordKnownOnServer?: boolean;
  grupoId?: string | number | null;
  empresaId?: string | number | null;
}) {
  const d = normalizeQuoFacturitaDraftFromEmpresaConfig(config ?? null);
  const showPw = Boolean(d.contrasena) || Boolean(passwordKnownOnServer);
  const hasAny = Boolean(d.usuario || showPw);
  if (!hasAny) return <div style={placeholderPanelStyle}>Sin datos QUO Facturita.</div>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={infoEmpresaFieldRowStyle}>
        <span style={infoEmpresaLabelStyle}>Usuario</span>
        <div style={infoEmpresaControlColStyle}>
          <input
            type="text"
            readOnly
            style={inputReadOnlyStyle}
            value={d.usuario || ''}
            placeholder="—"
            aria-readonly
            autoComplete="off"
            spellCheck={false}
          />
        </div>
      </div>
      <div style={infoEmpresaFieldRowStyle}>
        <span style={infoEmpresaLabelStyle}>Contraseña</span>
        <div style={infoEmpresaControlColStyle}>
          <EmpresaPwdCopyBlock
            localPassword={d.contrasena}
            passwordKey="facturita"
            grupoId={grupoId}
            empresaId={empresaId}
            ariaLabel="Copiar contraseña QUO Facturita"
          >
            <input
              type="password"
              readOnly
              style={inputReadOnlyStyle}
              value={showPw ? '********' : ''}
              placeholder={showPw ? undefined : '—'}
              aria-readonly
              autoComplete="off"
              spellCheck={false}
            />
          </EmpresaPwdCopyBlock>
        </div>
      </div>
    </div>
  );
}

function MasInfoQuoContabilitaForm({
  disabled,
  draft,
  setDraft,
  onCancel,
  onAccept,
  onContrasenaEdited,
  grupoId,
  empresaId,
}: {
  disabled: boolean;
  draft: QuoContabilitaFormValues;
  setDraft: React.Dispatch<React.SetStateAction<QuoContabilitaFormValues>>;
  onCancel: () => void;
  onAccept: () => void;
  /** Se llama al cambiar el campo contraseña (abre el formulario con contraseña vacía; marcar si el usuario escribe). */
  onContrasenaEdited?: () => void;
  grupoId?: string | number | null;
  empresaId?: string | number | null;
}) {
  const idQuo = 'masinfo-qco-quoid';
  const idUsu = 'masinfo-qco-usuario';
  const idPwd = 'masinfo-qco-contrasena';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <p className="profase-form-section-title" style={{ fontSize: 12 }}>QUO Contabilita</p>
      <div style={infoEmpresaFieldRowStyle}>
        <label style={infoEmpresaLabelStyle} htmlFor={idQuo}>
          QUOId
        </label>
        <div style={infoEmpresaControlColStyle}>
          <input
            id={idQuo}
            type="text"
            inputMode="text"
            style={inputStyle}
            value={draft.quoId}
            disabled={disabled}
            placeholder="Identificador QUO"
            autoComplete="off"
            spellCheck={false}
            onChange={e => setDraft(p => ({ ...p, quoId: e.target.value }))}
          />
        </div>
      </div>
      <div style={infoEmpresaFieldRowStyle}>
        <label style={infoEmpresaLabelStyle} htmlFor={idUsu}>
          Usuario
        </label>
        <div style={infoEmpresaControlColStyle}>
          <input
            id={idUsu}
            type="text"
            inputMode="text"
            style={inputStyle}
            value={draft.usuario}
            disabled={disabled}
            placeholder="Usuario"
            autoComplete="off"
            spellCheck={false}
            onChange={e => setDraft(p => ({ ...p, usuario: e.target.value }))}
          />
        </div>
      </div>
      <div style={infoEmpresaFieldRowStyle}>
        <label style={infoEmpresaLabelStyle} htmlFor={idPwd}>
          Contraseña
        </label>
        <div style={infoEmpresaControlColStyle}>
          <EmpresaPwdCopyBlock
            localPassword={draft.contrasena}
            disabled={disabled}
            passwordKey="contabilita"
            grupoId={grupoId}
            empresaId={empresaId}
            ariaLabel="Copiar contraseña QUO Contabilita"
          >
            <input
              id={idPwd}
              type="password"
              inputMode="text"
              style={inputStyle}
              value={draft.contrasena}
              disabled={disabled}
              placeholder="Nueva contraseña"
              autoComplete="new-password"
              spellCheck={false}
              onChange={e => {
                onContrasenaEdited?.();
                setDraft(p => ({ ...p, contrasena: e.target.value }));
              }}
            />
          </EmpresaPwdCopyBlock>
        </div>
      </div>
      <div style={masInfoFormFooterStyle}>
        <button type="button" style={btnSecondary} disabled={disabled} onClick={onCancel}>
          Cancelar
        </button>
        <button type="button" style={btnPrimary(disabled)} disabled={disabled} onClick={onAccept}>
          Aceptar
        </button>
      </div>
    </div>
  );
}

function MasInfoQuoFacturitaForm({
  disabled,
  draft,
  setDraft,
  onCancel,
  onAccept,
  onContrasenaEdited,
  grupoId,
  empresaId,
}: {
  disabled: boolean;
  draft: QuoFacturitaFormValues;
  setDraft: React.Dispatch<React.SetStateAction<QuoFacturitaFormValues>>;
  onCancel: () => void;
  onAccept: () => void;
  onContrasenaEdited?: () => void;
  grupoId?: string | number | null;
  empresaId?: string | number | null;
}) {
  const idUsu = 'masinfo-qfa-usuario';
  const idPwd = 'masinfo-qfa-contrasena';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <p className="profase-form-section-title" style={{ fontSize: 12 }}>QUO Facturita</p>
      <div style={infoEmpresaFieldRowStyle}>
        <label style={infoEmpresaLabelStyle} htmlFor={idUsu}>
          Usuario
        </label>
        <div style={infoEmpresaControlColStyle}>
          <input
            id={idUsu}
            type="text"
            inputMode="text"
            style={inputStyle}
            value={draft.usuario}
            disabled={disabled}
            placeholder="Usuario"
            autoComplete="off"
            spellCheck={false}
            onChange={e => setDraft(p => ({ ...p, usuario: e.target.value }))}
          />
        </div>
      </div>
      <div style={infoEmpresaFieldRowStyle}>
        <label style={infoEmpresaLabelStyle} htmlFor={idPwd}>
          Contraseña
        </label>
        <div style={infoEmpresaControlColStyle}>
          <EmpresaPwdCopyBlock
            localPassword={draft.contrasena}
            disabled={disabled}
            passwordKey="facturita"
            grupoId={grupoId}
            empresaId={empresaId}
            ariaLabel="Copiar contraseña QUO Facturita"
          >
            <input
              id={idPwd}
              type="password"
              inputMode="text"
              style={inputStyle}
              value={draft.contrasena}
              disabled={disabled}
              placeholder="Nueva contraseña"
              autoComplete="new-password"
              spellCheck={false}
              onChange={e => {
                onContrasenaEdited?.();
                setDraft(p => ({ ...p, contrasena: e.target.value }));
              }}
            />
          </EmpresaPwdCopyBlock>
        </div>
      </div>
      <div style={masInfoFormFooterStyle}>
        <button type="button" style={btnSecondary} disabled={disabled} onClick={onCancel}>
          Cancelar
        </button>
        <button type="button" style={btnPrimary(disabled)} disabled={disabled} onClick={onAccept}>
          Aceptar
        </button>
      </div>
    </div>
  );
}

/** Fecha-hora API (p. ej. alta de archivo) para tabla. */
function formatArchivoFechaAlta(v: unknown): string {
  const s = str(v).trim();
  if (!s) return '—';
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' });
  }
  return s;
}

function archivoCatalogoDescripcion(row: any): string {
  const cat = row?.archivoSAT ?? row?.archivoSello ?? row?.archivoSellos;
  return str(cat?.descripcion).trim() || '—';
}

/** GET …/empresa/:empresaId/archivo/:archivoId → blob y descarga en el navegador. */
async function downloadEmpresaArchivoBlob(params: {
  grupoId: string | number;
  empresaId: string | number;
  archivoId: string | number;
  fallbackNombre?: string;
}): Promise<void> {
  const { grupoId, empresaId, archivoId, fallbackNombre } = params;
  const url = `api/grupo-empresarials/${grupoId}/empresa/${empresaId}/archivo/${archivoId}`;
  const res = await axios.get(url, { responseType: 'blob' });
  const blob = res.data as Blob;
  let filename = str(fallbackNombre).trim() || 'archivo';
  const cd = res.headers['content-disposition'];
  if (typeof cd === 'string') {
    const star = /filename\*=UTF-8''([^;\s]+)/i.exec(cd);
    if (star?.[1]) {
      try {
        filename = decodeURIComponent(star[1].replace(/"/g, ''));
      } catch {
        filename = star[1];
      }
    } else {
      const quoted = /filename="([^"]+)"/i.exec(cd);
      const plain = quoted ?? /filename=([^;\s]+)/i.exec(cd);
      if (plain?.[1]) filename = plain[1].replace(/"/g, '');
    }
  }
  const href = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = href;
    a.download = filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(href);
  }
}

/** API: `createdBy` puede ser objeto usuario (`nombreCompleto`) o id primitivo. */
function archivoCreatedByLabel(row: any): string {
  const cb = row?.createdBy;
  if (cb == null || cb === '') return '';
  if (typeof cb === 'object') {
    const name = str(cb.nombreCompleto).trim();
    if (name) return name;
    const parts = [str(cb.nombre).trim(), str(cb.apellido).trim()].filter(Boolean);
    if (parts.length) return parts.join(' ');
    const u = str(cb.usuario).trim();
    if (u) return u;
    if (cb.id != null && cb.id !== '') return String(cb.id);
    return '';
  }
  return String(cb);
}

function ArchivosEmpresaTable({
  rows,
  loading,
  emptyLabel,
  tipoArchivoColumnTitle = 'Tipo Archivo SAT',
  disabled,
  grupoId,
  empresaId,
  onRemoveRow,
}: {
  rows: any[];
  loading: boolean;
  emptyLabel: string;
  tipoArchivoColumnTitle?: string;
  disabled: boolean;
  grupoId: string | number;
  empresaId: string | number;
  onRemoveRow: (index: number) => void;
}) {
  const [downloadBusyKey, setDownloadBusyKey] = useState<string | null>(null);

  if (loading) return <div style={placeholderPanelStyle}>Cargando…</div>;
  if (!rows.length) return <div style={placeholderPanelStyle}>{emptyLabel}</div>;
  return (
    <table className="profase-empresa-edit-table">
      <thead>
        <tr>
          <th>Nombre de archivo</th>
          <th style={{ width: 128 }}>{tipoArchivoColumnTitle}</th>
          <th style={{ minWidth: 120 }}>Subió</th>
          <th style={{ width: 138 }}>Fecha Alta</th>
          <th style={{ width: 56, textAlign: 'center' }} title="Descargar">
            Descarga
          </th>
          <th style={{ width: 72, textAlign: 'center' }}>Eliminar</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, idx) => {
          const archivoId = row?.id;
          const hasPersistedId = archivoId != null && archivoId !== '';
          const rowKey = hasPersistedId ? String(archivoId) : `ar-${idx}`;
          const downloadKey = rowKey;
          const downloadBusy = downloadBusyKey === downloadKey;
          return (
            <tr key={rowKey}>
              <td style={{ wordBreak: 'break-all' as const }}>
                {str(row.nombreArchivo).trim() || '—'}
              </td>
              <td>{archivoCatalogoDescripcion(row)}</td>
              <td>{archivoCreatedByLabel(row) || '—'}</td>
              <td>{formatArchivoFechaAlta(row.created)}</td>
              <td style={{ textAlign: 'center' }}>
                <button
                  type="button"
                  className="profase-form-btn-tiny-accent"
                  style={{
                    opacity: disabled || !hasPersistedId || downloadBusy ? 0.5 : 1,
                    cursor:
                      disabled || !hasPersistedId || downloadBusy ? 'not-allowed' : 'pointer',
                  }}
                  disabled={disabled || !hasPersistedId || downloadBusy}
                  title={hasPersistedId ? 'Descargar archivo' : 'Disponible tras guardar'}
                  onClick={async () => {
                    if (!hasPersistedId || downloadBusy) return;
                    setDownloadBusyKey(downloadKey);
                    try {
                      await downloadEmpresaArchivoBlob({
                        grupoId,
                        empresaId,
                        archivoId,
                        fallbackNombre: str(row.nombreArchivo),
                      });
                    } catch {
                      window.alert('No se pudo descargar el archivo.');
                    } finally {
                      setDownloadBusyKey(null);
                    }
                  }}
                >
                  <FaDownload />
                </button>
              </td>
              <td style={{ textAlign: 'center' }}>
                <button
                  type="button"
                  className="profase-form-btn-tiny-danger"
                  style={{
                    opacity: disabled ? 0.5 : 1,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                  }}
                  disabled={disabled}
                  title="Eliminar fila"
                  onClick={() => onRemoveRow(idx)}
                >
                  <FaTrash />
                </button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** Extrae lista del body de búsqueda de régimenes (varios formatos REST comunes). */
function regimenSearchResultsFromResponse(data: unknown): any[] {
  if (data == null) return [];
  if (Array.isArray(data)) return data;
  if (typeof data !== 'object') return [];
  const o = data as Record<string, unknown>;
  for (const key of ['content', 'data', 'results', 'items', 'regimenes']) {
    const v = o[key];
    if (Array.isArray(v)) return v;
  }
  const embedded = o._embedded;
  if (embedded && typeof embedded === 'object') {
    for (const v of Object.values(embedded as Record<string, unknown>)) {
      if (Array.isArray(v)) return v;
    }
  }
  return [];
}

/** Autocompletar catálogo `GET api/regimen?query=` */
function RegimenSearchAdd({
  disabled,
  onSelect,
}: {
  disabled: boolean;
  onSelect: (regimenEntity: any) => void;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<any[]>([]);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const syncMenuPosition = useCallback(() => {
    const el = wrapRef.current;
    if (!el) {
      setMenuPos(null);
      return;
    }
    const r = el.getBoundingClientRect();
    setMenuPos({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 200) });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    syncMenuPosition();
  }, [open, options.length, loading, query, syncMenuPosition]);

  useEffect(() => {
    if (!open) return;
    const update = () => syncMenuPosition();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open, syncMenuPosition]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (wrapRef.current?.contains(t) || dropdownRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    abortRef.current?.abort();
    const q = query.trim();
    if (q.length < 1) {
      setOptions([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      abortRef.current = new AbortController();
      setLoading(true);
      try {
        const res = await axios.get<any>('api/regimen', {
          params: { query: q },
          signal: abortRef.current.signal,
        });
        const arr = regimenSearchResultsFromResponse(res.data);
        setOptions(arr);
        setOpen(true);
      } catch (e: any) {
        if (e?.name !== 'CanceledError' && e?.code !== 'ERR_CANCELED') {
          setOptions([]);
          setOpen(true);
        }
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  const pick = (item: any) => {
    onSelect(item);
    setQuery('');
    setOptions([]);
    setOpen(false);
  };

  const regimenOptionLabel = (opt: any) =>
    str(opt?.valor ?? opt?.descripcion ?? opt?.codigo ?? opt?.nombre).trim() || '—';

  const showDropdown = open && menuPos && !loading && query.trim().length >= 1;

  const dropdown =
    showDropdown && menuPos ? (
      <div
        ref={dropdownRef}
        style={{
          position: 'fixed',
          top: menuPos.top,
          left: menuPos.left,
          width: menuPos.width,
          zIndex: 100001,
          boxSizing: 'border-box',
        }}
      >
        {options.length > 0 ? (
          <ul className="profase-form-dropdown">
            {options.map((opt, i) => (
              <li
                key={`${opt?.id ?? 'x'}-${i}`}
                onMouseDown={ev => {
                  ev.preventDefault();
                  pick(opt);
                }}
                className="profase-form-dropdown-item"
                style={{ fontSize: 12 }}
              >
                {regimenOptionLabel(opt)}
              </li>
            ))}
          </ul>
        ) : (
          <div className="profase-form-dropdown-empty" style={{ fontSize: 12 }}>
            Sin resultados
          </div>
        )}
      </div>
    ) : null;

  return (
    <>
      <div ref={wrapRef} style={{ position: 'relative', width: '100%' }}>
        <input
          id="empresa-regimen-buscar"
          style={inputStyle}
          value={query}
          disabled={disabled}
          placeholder="Buscar régimen…"
          autoComplete="off"
          onChange={e => setQuery(e.target.value)}
          onFocus={() => {
            if (query.trim().length >= 1) setOpen(true);
          }}
        />
        {loading && (
          <span className="profase-form-muted-text" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>Buscando…</span>
        )}
      </div>
      {dropdown ? createPortal(dropdown, document.body) : null}
    </>
  );
}

function mergeEmpresaConfig(prev: EmpresaConfigApi | null, incoming: EmpresaConfigApi): EmpresaConfigApi {
  if (!prev) return incoming;
  return {
    ...prev,
    ...incoming,
    info:
      incoming.info != null
        ? {
            ...(prev.info ?? {}),
            ...incoming.info,
            // La importación suele omitir o anular id; sin id el submit no envía el PUT.
            id: incoming.info.id ?? prev.info?.id,
          }
        : prev.info,
    regimenes: incoming.regimenes ?? prev.regimenes,
    actividadesEconomicas: incoming.actividadesEconomicas ?? prev.actividadesEconomicas,
    obligacionesFiscales: incoming.obligacionesFiscales ?? prev.obligacionesFiscales,
    archivosSat: incoming.archivosSat ?? prev.archivosSat,
    archivosSellos: incoming.archivosSellos ?? prev.archivosSellos,
    quoContabilita: incoming.quoContabilita ?? prev.quoContabilita,
    quoFacturita: incoming.quoFacturita ?? prev.quoFacturita,
    usuariosQUO: incoming.usuariosQUO ?? prev.usuariosQUO,
    usuariosQUOFacturita: incoming.usuariosQUOFacturita ?? prev.usuariosQUOFacturita,
  };
}

export interface EmpresaEditarModalProps {
  open: boolean;
  forceHidden?: boolean;
  grupoId: string | undefined;
  empresaId: string | undefined;
  onClose: () => void;
  onSaved?: () => void;
}

export default function EmpresaEditarModal({
  open,
  forceHidden = false,
  grupoId,
  empresaId,
  onClose,
  onSaved,
}: EmpresaEditarModalProps) {
  const dispatch = useAppDispatch();
  const updating = useAppSelector(s => s.grupoEmpresarial.updating);
  const empresasGrupo = useAppSelector(s => s.grupoEmpresarial.empresas) as any[];
  const empresasGrupoRef = useRef(empresasGrupo);
  empresasGrupoRef.current = empresasGrupo;

  const [empresaConfig, setEmpresaConfig] = useState<EmpresaConfigApi | null>(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const saveErrorRef = useRef<HTMLDivElement>(null);

  const [form, setForm] = useState<EmpresaEditarFormValues>({
    razonSocial: '',
    rfc: '',
  });
  /** `info.ccsellos`: solo `true` muestra Sí; `null` y `false` muestran No */
  const [ccSellosSi, setCcSellosSi] = useState(false);
  /** `info.csellos` (clave sellos) */
  const [claveSellos, setClaveSellos] = useState('');
  /** `info.vsellos` como yyyy-MM-dd para el date picker */
  const [vencimientoSellos, setVencimientoSellos] = useState('');
  /** `info.ccfiel`: solo `true` muestra Sí */
  const [ccFielSi, setCcFielSi] = useState(false);
  /** `info.cfiel` (clave FIEL) */
  const [claveFiel, setClaveFiel] = useState('');
  /** `info.vfiel` como yyyy-MM-dd */
  const [vencimientoFiel, setVencimientoFiel] = useState('');
  /** `info.ccciec`: solo `true` muestra Sí */
  const [ccCiecSi, setCcCiecSi] = useState(false);
  /** `info.cciec` (clave CIEC) */
  const [claveCiec, setClaveCiec] = useState('');
  /** `info.inicioO` Inicio operación (solo fecha) */
  const [inicioOperacion, setInicioOperacion] = useState('');
  /** `info.estatus`: 0 Inactivo, 1 Activo, 2 Incubado */
  const [estatusCodigo, setEstatusCodigo] = useState<'' | '0' | '1' | '2'>('');
  /** Empresa persona física u «Otro» como representante legal (moral). */
  const [representanteLegalSelection, setRepresentanteLegalSelection] = useState('');
  const [representanteLegalRfc, setRepresentanteLegalRfc] = useState('');
  const [representanteLegalRazonSocial, setRepresentanteLegalRazonSocial] = useState('');
  /** Filas de `config.regimenes` (GET/PUT); altas locales con `id: null`. */
  const [regimenesRows, setRegimenesRows] = useState<any[]>([]);
  const [actividadesEconomicasRows, setActividadesEconomicasRows] = useState<any[]>([]);
  const [obligacionesFiscalesRows, setObligacionesFiscalesRows] = useState<any[]>([]);
  const [domicilioForm, setDomicilioForm] = useState<DomicilioFormValues>({
    cp: '',
    tvialidad: '',
    ne: '',
    ni: '',
    localidad: '',
    ef: '',
    vialidad: '',
    colonia: '',
    municipio: '',
    entrec1: '',
    entrec2: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof EmpresaEditarFormValues, string>>>({});
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const [configRefreshTrigger, setConfigRefreshTrigger] = useState(0);
  const [importPdfBusy, setImportPdfBusy] = useState(false);
  const [importPdfError, setImportPdfError] = useState<string | null>(null);
  /** Aviso del backend cuando el import devuelve datos pero reporta warning (p. ej. HTTP no 2xx con body útil). */
  const [importPdfWarning, setImportPdfWarning] = useState<string | null>(null);
  const [importPdfFieldErrors, setImportPdfFieldErrors] = useState<ImportPdfFieldErrors>({});
  /** Pestaña activa en «Más información» (botón contextual en la barra inferior). */
  const [masInfoTab, setMasInfoTab] = useState('actividad-economica');
  /** list | form: tabla alta vía formulario en cada pestaña de Más información. */
  const [masInfoActividadUi, setMasInfoActividadUi] = useState<'list' | 'form'>('list');
  const [masInfoObligacionUi, setMasInfoObligacionUi] = useState<'list' | 'form'>('list');
  const [masInfoArchivoSatUi, setMasInfoArchivoSatUi] = useState<'list' | 'form'>('list');
  const [masInfoArchivoSellosUi, setMasInfoArchivoSellosUi] = useState<'list' | 'form'>('list');
  const [masInfoQuoContabilitaUi, setMasInfoQuoContabilitaUi] = useState<'list' | 'form'>('list');
  const [masInfoQuoFacturitaUi, setMasInfoQuoFacturitaUi] = useState<'list' | 'form'>('list');
  const [actividadDraft, setActividadDraft] = useState({
    inicioA: '',
    porcentaje: '',
    descripcionActividad: '',
  });
  const [obligacionDraft, setObligacionDraft] = useState({
    descripcionObligacion: '',
    descripcionVencimiento: '',
    fechaInicio: '',
  });
  const [archivoSatDraft, setArchivoSatDraft] = useState<MasInfoArchivoAltaDraft>({
    nombreArchivo: '',
    file: null,
    tipoId: '',
  });
  const [archivoSellosDraft, setArchivoSellosDraft] = useState<MasInfoArchivoAltaDraft>({
    nombreArchivo: '',
    file: null,
    tipoId: '',
  });
  const [archivoSatTiposCatalog, setArchivoSatTiposCatalog] = useState<ArchivoTipoCatalogOption[]>([]);
  const [archivoSatTiposLoading, setArchivoSatTiposLoading] = useState(false);
  const [archivoSatTiposError, setArchivoSatTiposError] = useState<string | null>(null);
  const [archivoSellosTiposCatalog, setArchivoSellosTiposCatalog] = useState<ArchivoTipoCatalogOption[]>([]);
  const [archivoSellosTiposLoading, setArchivoSellosTiposLoading] = useState(false);
  const [archivoSellosTiposError, setArchivoSellosTiposError] = useState<string | null>(null);
  const [quoContabilitaDraft, setQuoContabilitaDraft] = useState<QuoContabilitaFormValues>({
    quoId: '',
    usuario: '',
    contrasena: '',
  });
  const [quoFacturitaDraft, setQuoFacturitaDraft] = useState<QuoFacturitaFormValues>({
    usuario: '',
    contrasena: '',
  });
  /** `true` tras escribir/pegar en contraseña; al abrir formulario queda en `false` (campo vacío, no cuenta como cambio). */
  const quoContabilitaContrasenaDirtyRef = useRef(false);
  const quoFacturitaContrasenaDirtyRef = useRef(false);
  /** El GET no devuelve la contraseña real (p. ej. `*****`); solo indica si hay clave guardada en servidor (UI resumen). */
  const [quoContabilitaPwdOnServer, setQuoContabilitaPwdOnServer] = useState(false);
  const [quoFacturitaPwdOnServer, setQuoFacturitaPwdOnServer] = useState(false);
  /** Índice de fila en edición (null = alta) para actividad / obligación en Más información. */
  const [masInfoActividadEditIndex, setMasInfoActividadEditIndex] = useState<number | null>(null);
  const [masInfoObligacionEditIndex, setMasInfoObligacionEditIndex] = useState<number | null>(null);

  const empresaInfo = empresaConfig?.info;
  const showRepresentanteLegal = isEmpresaMoral(empresaInfo);

  const representanteLegalOptions = React.useMemo(() => {
    const currentEmpresaId =
      empresaInfo?.id != null ? Number(empresaInfo.id) : empresaId != null ? Number(empresaId) : NaN;
    return (empresasGrupo ?? [])
      .filter((e: any) => {
        if (e?.empresaFisica !== true || e?.id == null) return false;
        const id = Number(e.id);
        return !Number.isFinite(currentEmpresaId) || id !== currentEmpresaId;
      })
      .map((e: any) => ({
        value: String(e.id),
        label: String(e.razonSocial ?? e.nombre ?? `ID ${e.id}`).trim(),
        rfc: str(e.rfc),
        razonSocial: str(e.razonSocial),
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'es'));
  }, [empresasGrupo, empresaInfo?.id, empresaId]);

  const representanteLegalEsOtro = representanteLegalSelection === REPRESENTANTE_LEGAL_OTRO;

  const handleRepresentanteLegalSelectionChange = (value: string) => {
    setRepresentanteLegalSelection(value);
    if (value === REPRESENTANTE_LEGAL_OTRO) {
      setRepresentanteLegalRfc('');
      setRepresentanteLegalRazonSocial('');
      return;
    }
    if (value === '') {
      setRepresentanteLegalRfc('');
      setRepresentanteLegalRazonSocial('');
      return;
    }
    setRepresentanteLegalRfc('');
    setRepresentanteLegalRazonSocial('');
  };

  const applyEmpresaConfigFromApi = useCallback((data: EmpresaConfigApi) => {
    setQuoContabilitaPwdOnServer(empresaConfigQuoContabilitaShowsStoredPassword(data));
    setQuoFacturitaPwdOnServer(empresaConfigQuoFacturitaShowsStoredPassword(data));
    setEmpresaConfig(sanitizeEmpresaConfigQuoEchoPasswordsForClientState(data));
    const info = data?.info;
    if (info) {
      setCcSellosSi(info.ccsellos === true);
      setClaveSellos(str(info.csellos));
      setVencimientoSellos(vsellosToDateInput(info.vsellos));
      setCcFielSi(info.ccfiel === true);
      setClaveFiel(str(info.cfiel));
      setVencimientoFiel(vsellosToDateInput(info.vfiel));
      setCcCiecSi(info.ccciec === true);
      setClaveCiec(str(info.cciec));
      setInicioOperacion(vsellosToDateInput(info.inicioO));
      setEstatusCodigo(estatusInfoToSelectValue(info));
      const rep = representanteLegalFromInfo(info, empresasGrupoRef.current);
      setRepresentanteLegalSelection(rep.selection);
      setRepresentanteLegalRfc(rep.rfc);
      setRepresentanteLegalRazonSocial(rep.razonSocial);
      setForm({
        razonSocial: str(info.razonSocial),
        rfc: str(info.rfc),
      });
      setDomicilioForm({
        cp: str(info.cp ?? info.codigoPostal),
        tvialidad: str(info.tvialidad),
        ne: str(info.ne ?? info.numeroExterior),
        ni: str(info.ni ?? info.numeroInterior),
        localidad: str(info.localidad),
        ef: str(info.ef ?? info.estado),
        vialidad: str(info.vialidad ?? info.calle),
        colonia: str(info.colonia),
        municipio: str(info.municipio),
        entrec1: str(info.entrec1),
        entrec2: str(info.entrec2),
      });
    }
    setRegimenesRows(Array.isArray(data.regimenes) ? data.regimenes.map((r: any) => ({ ...r })) : []);
    setActividadesEconomicasRows(
      Array.isArray(data.actividadesEconomicas) ? data.actividadesEconomicas.map((r: any) => ({ ...r })) : [],
    );
    setObligacionesFiscalesRows(
      Array.isArray(data.obligacionesFiscales) ? data.obligacionesFiscales.map((r: any) => ({ ...r })) : [],
    );
  }, []);

  useEffect(() => {
    if (!open || !grupoId || grupoId === 'new' || !empresaId || empresaId === 'new') {
      setEmpresaConfig(null);
      setQuoContabilitaPwdOnServer(false);
      setQuoFacturitaPwdOnServer(false);
      setConfigError(null);
      setConfigLoading(false);
      return;
    }

    let cancelled = false;
    setSaveError(null);
    setErrors({});
    setConfigError(null);
    setImportPdfError(null);
    setImportPdfWarning(null);
    setImportPdfFieldErrors({});
    setEmpresaConfig(null);
    setQuoContabilitaPwdOnServer(false);
    setQuoFacturitaPwdOnServer(false);
    setCcSellosSi(false);
    setClaveSellos('');
    setVencimientoSellos('');
    setCcFielSi(false);
    setClaveFiel('');
    setVencimientoFiel('');
    setInicioOperacion('');
    setEstatusCodigo('');
    setRepresentanteLegalSelection('');
    setRepresentanteLegalRfc('');
    setRepresentanteLegalRazonSocial('');
    setRegimenesRows([]);
    setActividadesEconomicasRows([]);
    setObligacionesFiscalesRows([]);
    setMasInfoTab('actividad-economica');
    setMasInfoActividadUi('list');
    setMasInfoObligacionUi('list');
    setMasInfoArchivoSatUi('list');
    setMasInfoArchivoSellosUi('list');
    setMasInfoQuoContabilitaUi('list');
    setMasInfoQuoFacturitaUi('list');
    setActividadDraft({ inicioA: '', porcentaje: '', descripcionActividad: '' });
    setObligacionDraft({ descripcionObligacion: '', descripcionVencimiento: '', fechaInicio: '' });
    setArchivoSatDraft({ nombreArchivo: '', file: null, tipoId: '' });
    setArchivoSellosDraft({ nombreArchivo: '', file: null, tipoId: '' });
    setArchivoSatTiposCatalog([]);
    setArchivoSatTiposError(null);
    setArchivoSatTiposLoading(false);
    setArchivoSellosTiposCatalog([]);
    setArchivoSellosTiposError(null);
    setArchivoSellosTiposLoading(false);
    setQuoContabilitaDraft({ quoId: '', usuario: '', contrasena: '' });
    setQuoFacturitaDraft({ usuario: '', contrasena: '' });
    setMasInfoActividadEditIndex(null);
    setMasInfoObligacionEditIndex(null);
    setConfigLoading(true);

    dispatch(getEmpresasByGrupo({ id: grupoId, page: 0, size: 99999 }));

    dispatch(getEmpresaConfigEnGrupo({ grupoId, empresaId }))
      .unwrap()
      .then(res => {
        if (cancelled) return;
        const raw = res?.data ?? res;
        const normalized = normalizeEmpresaConfigPayload(raw);
        const data: EmpresaConfigApi = normalized ?? (raw as EmpresaConfigApi);
        applyEmpresaConfigFromApi(data);
      })
      .catch(err => {
        if (!cancelled) {
          const msg =
            err && typeof err === 'object' && 'message' in err && typeof (err as any).message === 'string'
              ? (err as any).message
              : 'No se pudo cargar la configuración de la empresa.';
          setConfigError(msg);
        }
      })
      .finally(() => {
        if (!cancelled) setConfigLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, grupoId, empresaId, configRefreshTrigger, dispatch, applyEmpresaConfigFromApi]);

  useEffect(() => {
    setMasInfoActividadUi('list');
    setMasInfoObligacionUi('list');
    setMasInfoArchivoSatUi('list');
    setMasInfoArchivoSellosUi('list');
    setMasInfoQuoContabilitaUi('list');
    setMasInfoQuoFacturitaUi('list');
    setActividadDraft({ inicioA: '', porcentaje: '', descripcionActividad: '' });
    setObligacionDraft({ descripcionObligacion: '', descripcionVencimiento: '', fechaInicio: '' });
    setArchivoSatDraft({ nombreArchivo: '', file: null, tipoId: '' });
    setArchivoSellosDraft({ nombreArchivo: '', file: null, tipoId: '' });
    setQuoContabilitaDraft({ quoId: '', usuario: '', contrasena: '' });
    setQuoFacturitaDraft({ usuario: '', contrasena: '' });
    setMasInfoActividadEditIndex(null);
    setMasInfoObligacionEditIndex(null);
  }, [masInfoTab]);

  useEffect(() => {
    if (!open || masInfoArchivoSatUi !== 'form') return;
    const ac = new AbortController();
    setArchivoSatTiposLoading(true);
    setArchivoSatTiposError(null);
    axios
      .get<unknown>('api/archivo-sats', { signal: ac.signal })
      .then(res => {
        setArchivoSatTiposCatalog(archivoTipoCatalogOptionsFromApiData(res.data));
      })
      .catch(err => {
        if (isAxiosError(err) && err.code === 'ERR_CANCELED') return;
        setArchivoSatTiposError('No se pudo cargar el catálogo de tipos SAT.');
        setArchivoSatTiposCatalog([]);
      })
      .finally(() => {
        if (!ac.signal.aborted) setArchivoSatTiposLoading(false);
      });
    return () => ac.abort();
  }, [open, masInfoArchivoSatUi]);

  useEffect(() => {
    if (!open || masInfoArchivoSellosUi !== 'form') return;
    const ac = new AbortController();
    setArchivoSellosTiposLoading(true);
    setArchivoSellosTiposError(null);
    axios
      .get<unknown>('api/archivo-sats', { signal: ac.signal })
      .then(res => {
        setArchivoSellosTiposCatalog(archivoTipoCatalogOptionsFromApiData(res.data));
      })
      .catch(err => {
        if (isAxiosError(err) && err.code === 'ERR_CANCELED') return;
        setArchivoSellosTiposError('No se pudo cargar el catálogo de tipos.');
        setArchivoSellosTiposCatalog([]);
      })
      .finally(() => {
        if (!ac.signal.aborted) setArchivoSellosTiposLoading(false);
      });
    return () => ac.abort();
  }, [open, masInfoArchivoSellosUi]);

  useEffect(() => {
    if (!saveError || !saveErrorRef.current) return;
    saveErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    saveErrorRef.current.focus({ preventScroll: true });
  }, [saveError]);

  const handleFieldChange = (name: keyof EmpresaEditarFormValues, value: string) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
    setImportPdfFieldErrors(prev => {
      const next: ImportPdfFieldErrors = { ...prev, [name]: undefined };
      if (name === 'razonSocial') next.razonSocialLevel = undefined;
      if (name === 'rfc') next.rfcLevel = undefined;
      return next;
    });
  };

  const handlePdfImportClick = () => {
    setImportPdfError(null);
    setImportPdfWarning(null);
    setImportPdfFieldErrors({});
    pdfInputRef.current?.click();
  };

  const handlePdfFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const file = input.files?.[0];
    input.value = '';
    if (!file || !grupoId || grupoId === 'new' || !empresaId || empresaId === 'new') return;
    setImportPdfError(null);
    setImportPdfWarning(null);
    setImportPdfFieldErrors({});
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setImportPdfError('Solo se permiten archivos .pdf');
      return;
    }
    if (file.type && file.type !== 'application/pdf') {
      setImportPdfError('El archivo debe ser PDF (application/pdf).');
      return;
    }
    if (!(await fileHasPdfMagic(file))) {
      setImportPdfError('El archivo no es un PDF válido (falta la cabecera %PDF).');
      return;
    }
    let pdfBytes: ArrayBuffer;
    try {
      pdfBytes = await file.arrayBuffer();
    } catch {
      setImportPdfError('No se pudo leer el archivo.');
      return;
    }
    setImportPdfBusy(true);
    try {
      const res = await dispatch(
        importEmpresaConfigPdf({
          grupoId,
          empresaId,
          pdfBytes,
        }),
      ).unwrap();
      const raw = (res as any)?.data !== undefined ? (res as any).data : res;
      const parsed = parseImportConfigBody(raw);
      if (parsed?.info) {
        const merged = mergeEmpresaConfig(empresaConfig, parsed);
        applyEmpresaConfigFromApi(merged);
        setConfigError(null);
        setImportPdfError(null);
        setImportPdfWarning(extractImportWarningFromBody(raw));
        setImportPdfFieldErrors(extractImportacionFieldErrors(raw));
      } else {
        setImportPdfWarning(null);
        setImportPdfFieldErrors({});
        setConfigRefreshTrigger(t => t + 1);
      }
    } catch (err: any) {
      const raw = err?.data ?? err;
      const parsed = parseImportConfigBody(raw);
      if (parsed?.info) {
        const merged = mergeEmpresaConfig(empresaConfig, parsed);
        applyEmpresaConfigFromApi(merged);
        setConfigError(null);
        setImportPdfError(null);
        setImportPdfWarning(extractImportWarningFromBody(raw));
        setImportPdfFieldErrors(extractImportacionFieldErrors(raw));
      } else {
        setImportPdfWarning(null);
        setImportPdfFieldErrors({});
        const msg =
          err && typeof err === 'object' && 'message' in err && typeof err.message === 'string'
            ? err.message
            : 'No se pudo importar el PDF.';
        setImportPdfError(msg);
      }
    } finally {
      setImportPdfBusy(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const info = empresaConfig?.info;
    if (!info) return;
    const persistedEmpresaId = info.id ?? empresaId;
    if (!empresaId || empresaId === 'new' || !grupoId || grupoId === 'new' || persistedEmpresaId == null || persistedEmpresaId === 'new') {
      return;
    }
    const errs: Partial<Record<keyof EmpresaEditarFormValues, string>> = {};
    if (!form.razonSocial.trim()) errs.razonSocial = 'La razón social es requerida';
    if (!form.rfc.trim()) errs.rfc = 'El RFC es requerido';
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setSaveError(null);
    /* No propagar secretos del GET vía spread: cfiel / csellos / cciec se envían solo si el usuario cambió el campo; si no, null. */
    const {
      cfiel: _omitCfiel,
      csellos: _omitCsellos,
      cciec: _omitCciec,
      ...infoRest
    } = info;
    const base: Record<string, any> = { ...infoRest };
    const empresaPayload: Record<string, unknown> = {
      ...base,
      id: persistedEmpresaId,
      razonSocial: form.razonSocial.trim(),
      rfc: form.rfc.trim(),
      cp: domicilioForm.cp.trim(),
      tvialidad: domicilioForm.tvialidad.trim(),
      ne: domicilioForm.ne.trim(),
      ni: domicilioForm.ni.trim(),
      localidad: domicilioForm.localidad.trim(),
      ef: domicilioForm.ef.trim(),
      vialidad: domicilioForm.vialidad.trim(),
      colonia: domicilioForm.colonia.trim(),
      municipio: domicilioForm.municipio.trim(),
      entrec1: domicilioForm.entrec1.trim(),
      entrec2: domicilioForm.entrec2.trim(),
      ccsellos: ccSellosSi,
      ccfiel: ccFielSi,
      ccciec: ccCiecSi,
    };
    const initialCsellos = str(info.csellos);
    const claveTrim = claveSellos.trim();
    if (claveTrim !== initialCsellos) {
      empresaPayload.csellos = claveTrim === '' ? null : claveTrim;
    } else {
      empresaPayload.csellos = null;
    }
    const initialVsellos = vsellosToDateInput(info.vsellos);
    const vsTrim = vencimientoSellos.trim();
    if (vsTrim !== initialVsellos) {
      empresaPayload.vsellos = vsTrim === '' ? null : vsTrim;
    }
    const initialCfiel = str(info.cfiel);
    const claveFielTrim = claveFiel.trim();
    if (claveFielTrim !== initialCfiel) {
      empresaPayload.cfiel = claveFielTrim === '' ? null : claveFielTrim;
    } else {
      /* No reenviar eco/máscara del GET; el backend interpreta sin cambio. */
      empresaPayload.cfiel = null;
    }
    const initialVfiel = vsellosToDateInput(info.vfiel);
    const vfTrim = vencimientoFiel.trim();
    if (vfTrim !== initialVfiel) {
      empresaPayload.vfiel = vfTrim === '' ? null : vfTrim;
    }
    const initialCciec = str(info.cciec);
    const claveCiecTrim = claveCiec.trim();
    if (claveCiecTrim !== initialCciec) {
      empresaPayload.cciec = claveCiecTrim === '' ? null : claveCiecTrim;
    } else {
      empresaPayload.cciec = null;
    }
    const initialInicioO = vsellosToDateInput(info.inicioO);
    const inicioTrim = inicioOperacion.trim();
    if (inicioTrim !== initialInicioO) {
      empresaPayload.inicioO = inicioTrim === '' ? null : inicioTrim;
    }
    const initialEstatus = estatusInfoToSelectValue(info);
    if (estatusCodigo !== initialEstatus) {
      empresaPayload.estatus = estatusCodigo === '' ? null : Number(estatusCodigo);
    }
    if (isEmpresaMoral(info)) {
      const initialRep = representanteLegalFromInfo(info, empresasGrupo);
      const isOtro = representanteLegalSelection === REPRESENTANTE_LEGAL_OTRO;
      const rfcTrim = representanteLegalRfc.trim();
      const razonTrim = representanteLegalRazonSocial.trim();

      if (representanteLegalSelection !== initialRep.selection) {
        if (representanteLegalSelection === '' || isOtro) {
          empresaPayload.representanteLegal = null;
        } else {
          empresaPayload.representanteLegal = { id: Number(representanteLegalSelection) };
        }
      }

      if (isOtro) {
        if (rfcTrim !== initialRep.rfc) {
          empresaPayload.representanteLegalRfc = rfcTrim === '' ? null : rfcTrim;
        }
        if (razonTrim !== initialRep.razonSocial) {
          empresaPayload.representanteLegalRazonSocial = razonTrim === '' ? null : razonTrim;
        }
      } else if (initialRep.selection === REPRESENTANTE_LEGAL_OTRO) {
        if (initialRep.rfc || initialRep.razonSocial) {
          empresaPayload.representanteLegalRfc = null;
          empresaPayload.representanteLegalRazonSocial = null;
        }
      }
    }
    const regimenesPayload = regimenesRows.map((row: any) => ({
      id: row.id ?? null,
      regimen: row.regimen,
      empresa: { id: persistedEmpresaId },
    }));
    const configBody: Record<string, unknown> = {
      ...(empresaConfig as EmpresaConfigApi),
      info: empresaPayload,
      regimenes: regimenesPayload,
      actividadesEconomicas: actividadesEconomicasRows,
      obligacionesFiscales: obligacionesFiscalesRows,
    };
    try {
      await dispatch(
        updateEmpresaConfigEnGrupo({
          grupoId,
          empresaId: persistedEmpresaId,
          config: configBody,
        }),
      ).unwrap();
      if (onSaved) {
        onSaved();
      } else {
        onClose();
      }
    } catch (err) {
      setSaveError(apiErrorMessage(err, 'No se pudo guardar la empresa.'));
    }
  };

  const masInfoContextualDisabled = updating || configLoading || importPdfBusy || !empresaConfig?.info;
  const masInfoFormBusy = configLoading || importPdfBusy || updating;
  /** Id empresa para GET …/key/contabilita | facturita (alineado con config / ruta). */
  const empresaIdForQuoKeyFetch = empresaConfig?.info?.id ?? empresaId;
  const masInfoInFormMode =
    masInfoActividadUi === 'form' ||
    masInfoObligacionUi === 'form' ||
    masInfoArchivoSatUi === 'form' ||
    masInfoArchivoSellosUi === 'form' ||
    masInfoQuoContabilitaUi === 'form' ||
    masInfoQuoFacturitaUi === 'form';

  function renderMasInfoContextualButton() {
    const common = {
      type: 'button' as const,
      style: btnSecondary,
      disabled: masInfoContextualDisabled,
    };
    switch (masInfoTab) {
      case 'actividad-economica':
        return (
          <button
            {...common}
            onClick={() => {
              setMasInfoActividadEditIndex(null);
              setActividadDraft({ inicioA: '', porcentaje: '', descripcionActividad: '' });
              setMasInfoActividadUi('form');
            }}
          >
            <FaPlus style={{ marginRight: 6 }} aria-hidden />
            Agregar Actividad Económica
          </button>
        );
      case 'obligaciones-fiscales':
        return (
          <button
            {...common}
            onClick={() => {
              setMasInfoObligacionEditIndex(null);
              setObligacionDraft({
                descripcionObligacion: '',
                descripcionVencimiento: '',
                fechaInicio: '',
              });
              setMasInfoObligacionUi('form');
            }}
          >
            <FaPlus style={{ marginRight: 6 }} aria-hidden />
            Agregar Obligación Fiscal
          </button>
        );
      case 'archivos-sat':
        return (
          <button
            {...common}
            onClick={() => {
              setArchivoSatDraft({ nombreArchivo: '', file: null, tipoId: '' });
              setMasInfoArchivoSatUi('form');
            }}
          >
            <FaPlus style={{ marginRight: 6 }} aria-hidden />
            Agregar Archivo SAT
          </button>
        );
      case 'archivos-sellos':
        return (
          <button
            {...common}
            onClick={() => {
              setArchivoSellosDraft({ nombreArchivo: '', file: null, tipoId: '' });
              setMasInfoArchivoSellosUi('form');
            }}
          >
            <FaPlus style={{ marginRight: 6 }} aria-hidden />
            Agregar Archivo Sellos
          </button>
        );
      case 'quo-contabilita':
        return (
          <button
            {...common}
            onClick={() => {
              const d = normalizeQuoContabilitaDraftFromEmpresaConfig(empresaConfig);
              quoContabilitaContrasenaDirtyRef.current = false;
              setQuoContabilitaDraft({ ...d, contrasena: '' });
              setMasInfoQuoContabilitaUi('form');
            }}
          >
            Cambiar QUO Contabilita
          </button>
        );
      case 'quo-facturita':
        return (
          <button
            {...common}
            onClick={() => {
              const d = normalizeQuoFacturitaDraftFromEmpresaConfig(empresaConfig);
              quoFacturitaContrasenaDirtyRef.current = false;
              setQuoFacturitaDraft({ ...d, contrasena: '' });
              setMasInfoQuoFacturitaUi('form');
            }}
          >
            Cambiar QUO Facturita
          </button>
        );
      default:
        return null;
    }
  }

  if (!open || !empresaId || empresaId === 'new' || !grupoId || grupoId === 'new') return null;

  if (forceHidden) return null;

  return (
    <ProfaseModal
      open
      titleId="empresa-editar-modal-title"
      size="wide"
      scroll
      onBackdropMouseDown={ev => {
        if (ev.target === ev.currentTarget && !updating && !configLoading && !importPdfBusy) onClose();
      }}
    >
        <h2 id="empresa-editar-modal-title" className="profase-modal-title" style={{ marginBottom: 14, fontSize: 17 }}>
          Editar empresa
        </h2>

        {configLoading && (
          <p className="profase-form-status-loading">Cargando configuración…</p>
        )}
        {configError && (
          <p className="profase-form-alert-error" style={{ margin: '0 0 10px' }}>{configError}</p>
        )}
        {importPdfError && (
          <p className="profase-form-alert-error" style={{ margin: '0 0 10px' }}>{importPdfError}</p>
        )}
        {importPdfWarning && (
          <p className="profase-form-alert-warning" style={{ margin: '0 0 10px' }}>{importPdfWarning}</p>
        )}

        <form onSubmit={handleSubmit}>
          {saveError ? (
            <div
              ref={saveErrorRef}
              tabIndex={-1}
              role="alert"
              aria-live="assertive"
              style={{ marginBottom: 16, outline: 'none' }}
            >
              <PageAlert
                alert={{
                  kind: 'toast',
                  variant: 'error',
                  message: saveError,
                  onDismiss: () => setSaveError(null),
                }}
              />
            </div>
          ) : null}
          <div style={infoRegimenStackStyle}>
            {/* 1. Info de Empresa: título vertical a la izquierda */}
            <div
              style={{
                ...sectionBoxStyle,
                marginBottom: 0,
                ...sectionWithVerticalTitleRowStyle,
              }}
            >
              <div style={sectionVerticalTitleBarStyle}>
                <h3 style={sectionVerticalTitleTextStyle}>Info de Empresa</h3>
              </div>
              <div style={{ ...sectionVerticalContentStyle, display: 'flex', flexDirection: 'column', gap: 7 }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '8px 14px',
                    alignItems: 'start',
                  }}
                >
                  <div style={infoEmpresaFieldRowStyle}>
                    <label style={infoEmpresaLabelStyle} htmlFor="empresa-edit-razon-social">
                      Razón Social <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <div style={infoEmpresaControlColStyle}>
                      <div style={empresaMainFieldCopyRowStyle}>
                        <input
                          style={{
                            ...inputStyle,
                            flex: 1,
                            minWidth: 0,
                            ...((): React.CSSProperties => {
                              if (errors.razonSocial) return { borderColor: 'var(--crm-form-error-text)', background: 'var(--crm-form-error-bg)' };
                              if (
                                importPdfFieldErrors.razonSocial &&
                                importPdfFieldErrors.razonSocialLevel?.toUpperCase() === 'WARNING'
                              ) {
                                return { borderColor: 'var(--crm-form-warning-border)', background: 'var(--crm-form-warning-bg)' };
                              }
                              return {};
                            })(),
                          }}
                          id="empresa-edit-razon-social"
                          value={form.razonSocial}
                          placeholder="Razón social"
                          disabled={configLoading}
                          autoComplete="organization"
                          onChange={e => handleFieldChange('razonSocial', e.target.value)}
                        />
                        <EmpresaTextCopyIconButton
                          text={form.razonSocial}
                          ariaLabel="Copiar razón social"
                          disabled={configLoading}
                        />
                      </div>
                      {(errors.razonSocial || importPdfFieldErrors.razonSocial) && (
                        <span
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 6,
                            fontSize: 11,
                            color: errors.razonSocial
                              ? '#ef4444'
                              : importPdfFieldErrors.razonSocialLevel?.toUpperCase() === 'WARNING'
                                ? '#b45309'
                                : '#ef4444',
                          }}
                        >
                          {!errors.razonSocial &&
                            importPdfFieldErrors.razonSocialLevel?.toUpperCase() === 'WARNING' && (
                              <FaExclamationTriangle style={{ flexShrink: 0, marginTop: 2 }} aria-hidden title="Advertencia" />
                            )}
                          <span>{errors.razonSocial ?? importPdfFieldErrors.razonSocial}</span>
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={infoEmpresaFieldRowStyle}>
                    <label style={infoEmpresaLabelStyle} htmlFor="empresa-edit-rfc">
                      RFC <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <div style={infoEmpresaControlColStyle}>
                      <div style={empresaMainFieldCopyRowStyle}>
                        <input
                          style={{
                            ...inputStyle,
                            flex: 1,
                            minWidth: 0,
                            ...((): React.CSSProperties => {
                              if (errors.rfc) return { borderColor: 'var(--crm-form-error-text)', background: 'var(--crm-form-error-bg)' };
                              if (
                                importPdfFieldErrors.rfc &&
                                importPdfFieldErrors.rfcLevel?.toUpperCase() === 'WARNING'
                              ) {
                                return { borderColor: 'var(--crm-form-warning-border)', background: 'var(--crm-form-warning-bg)' };
                              }
                              return {};
                            })(),
                          }}
                          id="empresa-edit-rfc"
                          value={form.rfc}
                          placeholder="RFC"
                          disabled={configLoading}
                          autoComplete="off"
                          spellCheck={false}
                          onChange={e => handleFieldChange('rfc', e.target.value)}
                        />
                        <EmpresaTextCopyIconButton text={form.rfc} ariaLabel="Copiar RFC" disabled={configLoading} />
                      </div>
                      {(errors.rfc || importPdfFieldErrors.rfc) && (
                        <span
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 6,
                            fontSize: 11,
                            color: errors.rfc
                              ? '#ef4444'
                              : importPdfFieldErrors.rfcLevel?.toUpperCase() === 'WARNING'
                                ? '#b45309'
                                : '#ef4444',
                          }}
                        >
                          {!errors.rfc && importPdfFieldErrors.rfcLevel?.toUpperCase() === 'WARNING' && (
                            <FaExclamationTriangle style={{ flexShrink: 0, marginTop: 2 }} aria-hidden title="Advertencia" />
                          )}
                          <span>{errors.rfc ?? importPdfFieldErrors.rfc}</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)',
                    gap: '8px 14px',
                    alignItems: 'start',
                  }}
                >
                  <div style={infoEmpresaFieldRowStyle}>
                    <label style={infoEmpresaLabelStyle} htmlFor="empresa-edit-ccsellos">
                      Cuenta con Sellos
                    </label>
                    <div style={infoEmpresaControlColStyle}>
                      <select
                        id="empresa-edit-ccsellos"
                        style={inputStyle}
                        value={ccSellosSi ? 'true' : 'false'}
                        disabled={configLoading}
                        onChange={e => setCcSellosSi(e.target.value === 'true')}
                      >
                        <option value="true">Sí</option>
                        <option value="false">No</option>
                      </select>
                    </div>
                  </div>
                  <div style={infoEmpresaFieldRowStyle}>
                    <label style={infoEmpresaLabelStyle} htmlFor="empresa-edit-csellos">
                      Clave Sellos
                    </label>
                    <div style={infoEmpresaControlColStyle}>
                      <div style={empresaMainFieldCopyRowStyle}>
                        <input
                          id="empresa-edit-csellos"
                          type="password"
                          autoComplete="new-password"
                          style={{ ...inputStyle, flex: 1, minWidth: 0 }}
                          value={claveSellos}
                          placeholder="Clave sellos"
                          disabled={configLoading}
                          onChange={e => setClaveSellos(e.target.value)}
                        />
                        <EmpresaDecodedKeySecretCopyIconButton
                          grupoId={grupoId}
                          empresaId={empresaIdForQuoKeyFetch}
                          localFallback={claveSellos}
                          disabled={configLoading}
                          keyPathSegment="clave-sellos"
                          pickKind="claveCiec"
                          ariaLabel="Copiar clave sellos"
                          titleWithApi="Obtiene la clave desde el servidor (GET …/key/clave-sellos, Base64) y copia al portapapeles"
                          titleLocalOnly="Copiar texto del campo Clave sellos"
                        />
                      </div>
                    </div>
                  </div>
                  <div style={infoEmpresaFieldRowStyle}>
                    <label style={infoEmpresaLabelStyle} htmlFor="empresa-edit-vsellos">
                      Vencimiento Sellos
                    </label>
                    <div style={infoEmpresaControlColStyle}>
                      <input
                        id="empresa-edit-vsellos"
                        type="date"
                        style={inputStyle}
                        value={vencimientoSellos}
                        disabled={configLoading}
                        onChange={e => setVencimientoSellos(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)',
                    gap: '8px 14px',
                    alignItems: 'start',
                  }}
                >
                  <div style={infoEmpresaFieldRowStyle}>
                    <label style={infoEmpresaLabelStyle} htmlFor="empresa-edit-ccfiel">
                      Cuenta con Fiel
                    </label>
                    <div style={infoEmpresaControlColStyle}>
                      <select
                        id="empresa-edit-ccfiel"
                        style={inputStyle}
                        value={ccFielSi ? 'true' : 'false'}
                        disabled={configLoading}
                        onChange={e => setCcFielSi(e.target.value === 'true')}
                      >
                        <option value="true">Sí</option>
                        <option value="false">No</option>
                      </select>
                    </div>
                  </div>
                  <div style={infoEmpresaFieldRowStyle}>
                    <label style={infoEmpresaLabelStyle} htmlFor="empresa-edit-cfiel">
                      Clave Fiel
                    </label>
                    <div style={infoEmpresaControlColStyle}>
                      <div style={empresaMainFieldCopyRowStyle}>
                        <input
                          id="empresa-edit-cfiel"
                          type="password"
                          autoComplete="new-password"
                          style={{ ...inputStyle, flex: 1, minWidth: 0 }}
                          value={claveFiel}
                          placeholder="Clave FIEL"
                          disabled={configLoading}
                          onChange={e => setClaveFiel(e.target.value)}
                        />
                        <EmpresaClaveFielCopyIconButton
                          grupoId={grupoId}
                          empresaId={empresaIdForQuoKeyFetch}
                          localFallback={claveFiel}
                          disabled={configLoading}
                        />
                      </div>
                    </div>
                  </div>
                  <div style={infoEmpresaFieldRowStyle}>
                    <label style={infoEmpresaLabelStyle} htmlFor="empresa-edit-vfiel">
                      Vencimiento Fiel
                    </label>
                    <div style={infoEmpresaControlColStyle}>
                      <input
                        id="empresa-edit-vfiel"
                        type="date"
                        style={inputStyle}
                        value={vencimientoFiel}
                        disabled={configLoading}
                        onChange={e => setVencimientoFiel(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
                    gap: '8px 14px',
                    alignItems: 'start',
                  }}
                >
                  <div style={infoEmpresaFieldRowStyle}>
                    <label style={infoEmpresaLabelStyle} htmlFor="empresa-edit-ccciec">
                      Cuenta con CIEC
                    </label>
                    <div style={infoEmpresaControlColStyle}>
                      <select
                        id="empresa-edit-ccciec"
                        style={inputStyle}
                        value={ccCiecSi ? 'true' : 'false'}
                        disabled={configLoading}
                        onChange={e => setCcCiecSi(e.target.value === 'true')}
                      >
                        <option value="true">Sí</option>
                        <option value="false">No</option>
                      </select>
                    </div>
                  </div>
                  <div style={infoEmpresaFieldRowStyle}>
                    <label style={infoEmpresaLabelStyle} htmlFor="empresa-edit-claveciec">
                      Clave CIEC
                    </label>
                    <div style={infoEmpresaControlColStyle}>
                      <div style={empresaMainFieldCopyRowStyle}>
                        <input
                          id="empresa-edit-claveciec"
                          type="password"
                          autoComplete="new-password"
                          style={{ ...inputStyle, flex: 1, minWidth: 0 }}
                          value={claveCiec}
                          placeholder="Clave CIEC"
                          disabled={configLoading}
                          onChange={e => setClaveCiec(e.target.value)}
                        />
                        <EmpresaDecodedKeySecretCopyIconButton
                          grupoId={grupoId}
                          empresaId={empresaIdForQuoKeyFetch}
                          localFallback={claveCiec}
                          disabled={configLoading}
                          keyPathSegment="clave-ciec"
                          pickKind="claveCiec"
                          ariaLabel="Copiar clave CIEC"
                          titleWithApi="Obtiene la clave CIEC del servidor (GET …/key/clave-ciec, Base64) y copia al portapapeles"
                          titleLocalOnly="Copiar texto del campo Clave CIEC"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
                    gap: '8px 14px',
                    alignItems: 'start',
                  }}
                >
                  <div style={infoEmpresaFieldRowStyle}>
                    <label style={infoEmpresaLabelStyle} htmlFor="empresa-edit-inicioO">
                      Inicio Operación
                    </label>
                    <div style={infoEmpresaControlColStyle}>
                      <input
                        id="empresa-edit-inicioO"
                        type="date"
                        style={inputStyle}
                        value={inicioOperacion}
                        disabled={configLoading}
                        onChange={e => setInicioOperacion(e.target.value)}
                      />
                    </div>
                  </div>
                  <div style={infoEmpresaFieldRowStyle}>
                    <label style={infoEmpresaLabelStyle} htmlFor="empresa-edit-estatus">
                      Estatus
                    </label>
                    <div style={infoEmpresaControlColStyle}>
                      <select
                        id="empresa-edit-estatus"
                        style={inputStyle}
                        value={estatusCodigo}
                        disabled={configLoading}
                        onChange={e => setEstatusCodigo(e.target.value as '' | '0' | '1' | '2')}
                      >
                        <option value="">—</option>
                        <option value="0">Inactivo</option>
                        <option value="1">Activo</option>
                        <option value="2">Incubado</option>
                      </select>
                    </div>
                  </div>
                </div>
                {showRepresentanteLegal ? (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: representanteLegalEsOtro
                        ? 'minmax(0, 1.15fr) minmax(0, 0.5fr) minmax(0, 1.35fr)'
                        : 'minmax(0, 1fr)',
                      gap: '8px 14px',
                      alignItems: 'start',
                    }}
                  >
                    <div style={infoEmpresaFieldRowStyle}>
                      <label style={infoEmpresaLabelStyle} htmlFor="empresa-edit-representante-legal">
                        Representante Legal
                      </label>
                      <div style={infoEmpresaControlColStyle}>
                        <select
                          id="empresa-edit-representante-legal"
                          style={inputStyle}
                          value={representanteLegalSelection}
                          disabled={configLoading}
                          onChange={e => handleRepresentanteLegalSelectionChange(e.target.value)}
                        >
                          <option value="">—</option>
                          <option value={REPRESENTANTE_LEGAL_OTRO}>Otro</option>
                          {representanteLegalSelection &&
                          representanteLegalSelection !== REPRESENTANTE_LEGAL_OTRO &&
                          !representanteLegalOptions.some(o => o.value === representanteLegalSelection) ? (
                            <option value={representanteLegalSelection}>
                              ID {representanteLegalSelection}
                            </option>
                          ) : null}
                          {representanteLegalOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {representanteLegalEsOtro ? (
                      <>
                        <div style={infoEmpresaFieldRowStyle}>
                          <label
                            style={representanteLegalRfcLabelStyle}
                            htmlFor="empresa-edit-representante-rfc"
                          >
                            RFC
                          </label>
                          <div style={infoEmpresaControlColStyle}>
                            <input
                              id="empresa-edit-representante-rfc"
                              style={inputStyle}
                              value={representanteLegalRfc}
                              disabled={configLoading}
                              onChange={e => setRepresentanteLegalRfc(e.target.value)}
                            />
                          </div>
                        </div>
                        <div style={infoEmpresaFieldRowStyle}>
                          <label
                            style={representanteLegalRazonSocialLabelStyle}
                            htmlFor="empresa-edit-representante-razon-social"
                          >
                            Razón Social
                          </label>
                          <div style={infoEmpresaControlColStyle}>
                            <input
                              id="empresa-edit-representante-razon-social"
                              style={inputStyle}
                              value={representanteLegalRazonSocial}
                              disabled={configLoading}
                              onChange={e => setRepresentanteLegalRazonSocial(e.target.value)}
                            />
                          </div>
                        </div>
                      </>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>

            {/* 2. Régimen (debajo, ancho completo); z-index para el menú del autocompletar sobre la siguiente sección */}
            <div
              style={{
                ...sectionBoxStyle,
                marginBottom: 0,
                ...sectionWithVerticalTitleRowStyle,
                position: 'relative',
                zIndex: 2,
              }}
            >
              <div style={sectionVerticalTitleBarStyle}>
                <h3 style={sectionVerticalTitleTextStyle}>Régimen</h3>
              </div>
              <div style={sectionVerticalContentStyle}>
              <table className="profase-empresa-edit-table">
                <tbody>
                  {regimenesRows.map((row, idx) => (
                    <tr key={`${row?.id ?? 'n'}-${idx}-${regimenRowValor(row).slice(0, 24)}`}>
                      <td>{regimenRowValor(row)}</td>
                      <td>
                        <button
                          type="button"
                          className="profase-form-btn-tiny-danger"
                          style={{
                            opacity: configLoading ? 0.5 : 1,
                            cursor: configLoading ? 'not-allowed' : 'pointer',
                          }}
                          disabled={configLoading}
                          title="Quitar"
                          onClick={() => setRegimenesRows(prev => prev.filter((_, i) => i !== idx))}
                        >
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {regimenesRows.length === 0 && !configLoading && (
                <p className="profase-form-muted-text" style={{ margin: '8px 0 0', fontSize: 11 }}>Sin regímenes. Use el buscador para agregar.</p>
              )}
              <div style={{ marginTop: 10 }}>
                {importPdfFieldErrors.regimen && (
                  <div
                    role="alert"
                    className={`profase-form-import-alert ${
                      importPdfFieldErrors.regimenLevel?.toUpperCase() === 'WARNING'
                        ? 'profase-form-import-alert--warning'
                        : 'profase-form-import-alert--error'
                    }`}
                  >
                    {importPdfFieldErrors.regimenLevel?.toUpperCase() === 'WARNING' && (
                      <FaExclamationTriangle style={{ flexShrink: 0, marginTop: 2 }} aria-hidden title="Advertencia" />
                    )}
                    <span>{importPdfFieldErrors.regimen}</span>
                  </div>
                )}
                <div style={infoEmpresaFieldRowStyle}>
                  <label style={infoEmpresaLabelStyle} htmlFor="empresa-regimen-buscar">
                    Agregar régimen:
                  </label>
                  <div style={infoEmpresaControlColStyle}>
                    <RegimenSearchAdd
                      disabled={
                        configLoading ||
                        importPdfBusy ||
                        Number.isNaN(Number(empresaConfig?.info?.id ?? empresaId))
                      }
                      onSelect={reg => {
                        const eid = Number(empresaConfig?.info?.id ?? empresaId);
                        setRegimenesRows(prev => [...prev, { id: null, regimen: reg, empresa: { id: eid } }]);
                        setImportPdfFieldErrors(prev => ({ ...prev, regimen: undefined, regimenLevel: undefined }));
                      }}
                    />
                  </div>
                </div>
              </div>
              </div>
            </div>
          </div>

          {/* 3. Domicilio Registrado */}
          <div style={{ ...sectionBoxStyle, ...sectionWithVerticalTitleRowStyle }}>
            <div style={sectionVerticalTitleBarStyle}>
              <h3 style={sectionVerticalTitleTextStyle}>Domicilio Registrado</h3>
            </div>
            <div style={sectionVerticalContentStyle}>
            <div style={domicilioRegistroGrid}>
              <div style={domicilioColumnStack}>
                <div style={domicilioTwinRowStyle}>
                  <div style={domicilioTwinCellStyle}>
                    <div style={infoEmpresaFieldRowStyle}>
                      <label style={domicilioLabelStyleCompact} htmlFor="domicilio-cp">
                        Código Postal
                      </label>
                      <div style={infoEmpresaControlColStyle}>
                        <input
                          id="domicilio-cp"
                          style={inputStyle}
                          value={domicilioForm.cp}
                          disabled={configLoading}
                          onChange={e => setDomicilioForm(p => ({ ...p, cp: e.target.value }))}
                        />
                      </div>
                    </div>
                  </div>
                  <div style={domicilioTwinCellStyle}>
                    <div style={infoEmpresaFieldRowStyle}>
                      <label style={domicilioLabelStyleCompact} htmlFor="domicilio-tvialidad">
                        Tipo Vialidad
                      </label>
                      <div style={infoEmpresaControlColStyle}>
                        <input
                          id="domicilio-tvialidad"
                          style={inputStyle}
                          value={domicilioForm.tvialidad}
                          disabled={configLoading}
                          onChange={e => setDomicilioForm(p => ({ ...p, tvialidad: e.target.value }))}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div style={domicilioTwinRowStyle}>
                  <div style={domicilioTwinCellStyle}>
                    <div style={infoEmpresaFieldRowStyle}>
                      <label style={domicilioLabelStyleCompact} htmlFor="domicilio-ne">
                        Número exterior
                      </label>
                      <div style={infoEmpresaControlColStyle}>
                        <input
                          id="domicilio-ne"
                          style={inputStyle}
                          value={domicilioForm.ne}
                          disabled={configLoading}
                          onChange={e => setDomicilioForm(p => ({ ...p, ne: e.target.value }))}
                        />
                      </div>
                    </div>
                  </div>
                  <div style={domicilioTwinCellStyle}>
                    <div style={infoEmpresaFieldRowStyle}>
                      <label style={domicilioLabelStyleCompact} htmlFor="domicilio-ni">
                        Número interior
                      </label>
                      <div style={infoEmpresaControlColStyle}>
                        <input
                          id="domicilio-ni"
                          style={inputStyle}
                          value={domicilioForm.ni}
                          disabled={configLoading}
                          onChange={e => setDomicilioForm(p => ({ ...p, ni: e.target.value }))}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div style={infoEmpresaFieldRowStyle}>
                  <label style={domicilioLabelStyle} htmlFor="domicilio-localidad">
                    Localidad
                  </label>
                  <div style={infoEmpresaControlColStyle}>
                    <input
                      id="domicilio-localidad"
                      style={inputStyle}
                      value={domicilioForm.localidad}
                      disabled={configLoading}
                      onChange={e => setDomicilioForm(p => ({ ...p, localidad: e.target.value }))}
                    />
                  </div>
                </div>
                <div style={infoEmpresaFieldRowStyle}>
                  <label style={domicilioLabelStyle} htmlFor="domicilio-ef">
                    Entidad Federativa
                  </label>
                  <div style={infoEmpresaControlColStyle}>
                    <input
                      id="domicilio-ef"
                      style={inputStyle}
                      value={domicilioForm.ef}
                      disabled={configLoading}
                      onChange={e => setDomicilioForm(p => ({ ...p, ef: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
              <div style={domicilioColumnStack}>
                <div style={infoEmpresaFieldRowStyle}>
                  <label style={domicilioLabelStyle} htmlFor="domicilio-vialidad">
                    Vialidad
                  </label>
                  <div style={infoEmpresaControlColStyle}>
                    <input
                      id="domicilio-vialidad"
                      style={inputStyle}
                      value={domicilioForm.vialidad}
                      disabled={configLoading}
                      onChange={e => setDomicilioForm(p => ({ ...p, vialidad: e.target.value }))}
                    />
                  </div>
                </div>
                <div style={infoEmpresaFieldRowStyle}>
                  <label style={domicilioLabelStyle} htmlFor="domicilio-colonia">
                    Colonia
                  </label>
                  <div style={infoEmpresaControlColStyle}>
                    <input
                      id="domicilio-colonia"
                      style={inputStyle}
                      value={domicilioForm.colonia}
                      disabled={configLoading}
                      onChange={e => setDomicilioForm(p => ({ ...p, colonia: e.target.value }))}
                    />
                  </div>
                </div>
                <div style={infoEmpresaFieldRowStyle}>
                  <label style={domicilioLabelStyle} htmlFor="domicilio-municipio">
                    Municipio
                  </label>
                  <div style={infoEmpresaControlColStyle}>
                    <input
                      id="domicilio-municipio"
                      style={inputStyle}
                      value={domicilioForm.municipio}
                      disabled={configLoading}
                      onChange={e => setDomicilioForm(p => ({ ...p, municipio: e.target.value }))}
                    />
                  </div>
                </div>
                <div style={domicilioTwinRowStyle}>
                  <div style={domicilioTwinCellStyle}>
                    <div style={infoEmpresaFieldRowStyle}>
                      <label style={domicilioLabelStyleCompact} htmlFor="domicilio-entrec1">
                        Entre Calle
                      </label>
                      <div style={infoEmpresaControlColStyle}>
                        <input
                          id="domicilio-entrec1"
                          style={inputStyle}
                          value={domicilioForm.entrec1}
                          disabled={configLoading}
                          onChange={e => setDomicilioForm(p => ({ ...p, entrec1: e.target.value }))}
                        />
                      </div>
                    </div>
                  </div>
                  <div style={domicilioTwinCellStyle}>
                    <div style={infoEmpresaFieldRowStyle}>
                      <label style={domicilioLabelStyleCompact} htmlFor="domicilio-entrec2">
                        Y calle
                      </label>
                      <div style={infoEmpresaControlColStyle}>
                        <input
                          id="domicilio-entrec2"
                          style={inputStyle}
                          value={domicilioForm.entrec2}
                          disabled={configLoading}
                          onChange={e => setDomicilioForm(p => ({ ...p, entrec2: e.target.value }))}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            </div>
          </div>

          {/* 4. Pestañas adicionales */}
          <div style={{ ...sectionBoxStyle, marginBottom: 0, ...sectionWithVerticalTitleRowStyle }}>
            <div style={sectionVerticalTitleBarStyle}>
              <h3 style={sectionVerticalTitleTextStyle}>Más información</h3>
            </div>
            <div style={sectionVerticalContentStyle}>
            <TabPanelContainer
              activeTab={masInfoTab}
              onTabChange={setMasInfoTab}
              accentColor={ACCENT}
              orientation="vertical"
              panelAreaStyle={{
                maxHeight: 'min(50vh, 440px)',
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
            >
              <TabPanel id="actividad-economica" label="Act. Económ.">
                {masInfoActividadUi === 'form' ? (
                  <MasInfoActividadForm
                    disabled={masInfoFormBusy}
                    draft={actividadDraft}
                    setDraft={setActividadDraft}
                    titulo={
                      masInfoActividadEditIndex !== null
                        ? 'Editar actividad económica'
                        : 'Nueva actividad económica'
                    }
                    onCancel={() => {
                      setMasInfoActividadUi('list');
                      setMasInfoActividadEditIndex(null);
                      setActividadDraft({ inicioA: '', porcentaje: '', descripcionActividad: '' });
                    }}
                    onAccept={() => {
                      const inicioA = actividadDraft.inicioA.trim() || null;
                      const pct = actividadDraft.porcentaje.trim();
                      let porcentaje: string | number | null = null;
                      if (pct !== '') {
                        const n = Number(pct.replace(/,/g, '.'));
                        porcentaje = Number.isNaN(n) ? pct : n;
                      }
                      const descripcionActividad = actividadDraft.descripcionActividad.trim();
                      const editIdx = masInfoActividadEditIndex;
                      setActividadesEconomicasRows(prev => {
                        if (editIdx !== null && editIdx >= 0 && editIdx < prev.length) {
                          const prevRow = prev[editIdx];
                          return prev.map((r, i) =>
                            i === editIdx
                              ? {
                                  ...prevRow,
                                  id: prevRow?.id ?? null,
                                  inicioA,
                                  porcentaje,
                                  descripcionActividad,
                                }
                              : r,
                          );
                        }
                        return [...prev, { id: null, inicioA, porcentaje, descripcionActividad }];
                      });
                      setMasInfoActividadEditIndex(null);
                      setMasInfoActividadUi('list');
                      setActividadDraft({ inicioA: '', porcentaje: '', descripcionActividad: '' });
                    }}
                  />
                ) : (
                  <ActividadEconomicaTable
                    rows={actividadesEconomicasRows}
                    loading={configLoading}
                    disabled={configLoading || importPdfBusy || updating}
                    onEditRow={idx => {
                      const row = actividadesEconomicasRows[idx];
                      if (!row) return;
                      setMasInfoActividadEditIndex(idx);
                      setActividadDraft({
                        inicioA: vsellosToDateInput(row.inicioA),
                        porcentaje:
                          row.porcentaje == null || row.porcentaje === '' ? '' : String(row.porcentaje),
                        descripcionActividad: str(row.descripcionActividad),
                      });
                      setMasInfoActividadUi('form');
                    }}
                    onRemoveRow={idx =>
                      setActividadesEconomicasRows(prev => prev.filter((_, i) => i !== idx))
                    }
                  />
                )}
              </TabPanel>
              <TabPanel id="obligaciones-fiscales" label="Obl. Fiscales">
                {masInfoObligacionUi === 'form' ? (
                  <MasInfoObligacionForm
                    disabled={masInfoFormBusy}
                    draft={obligacionDraft}
                    setDraft={setObligacionDraft}
                    titulo={
                      masInfoObligacionEditIndex !== null
                        ? 'Editar obligación fiscal'
                        : 'Nueva obligación fiscal'
                    }
                    onCancel={() => {
                      setMasInfoObligacionUi('list');
                      setMasInfoObligacionEditIndex(null);
                      setObligacionDraft({
                        descripcionObligacion: '',
                        descripcionVencimiento: '',
                        fechaInicio: '',
                      });
                    }}
                    onAccept={() => {
                      const descripcionObligacion = obligacionDraft.descripcionObligacion.trim();
                      const descripcionVencimiento = obligacionDraft.descripcionVencimiento.trim();
                      const fechaInicio = obligacionDraft.fechaInicio.trim() || null;
                      const editIdx = masInfoObligacionEditIndex;
                      setObligacionesFiscalesRows(prev => {
                        if (editIdx !== null && editIdx >= 0 && editIdx < prev.length) {
                          const prevRow = prev[editIdx];
                          return prev.map((r, i) =>
                            i === editIdx
                              ? {
                                  ...prevRow,
                                  id: prevRow?.id ?? null,
                                  descripcionObligacion,
                                  descripcionVencimiento,
                                  fechaInicio,
                                }
                              : r,
                          );
                        }
                        return [
                          ...prev,
                          {
                            id: null,
                            descripcionObligacion,
                            descripcionVencimiento,
                            fechaInicio,
                          },
                        ];
                      });
                      setMasInfoObligacionEditIndex(null);
                      setMasInfoObligacionUi('list');
                      setObligacionDraft({
                        descripcionObligacion: '',
                        descripcionVencimiento: '',
                        fechaInicio: '',
                      });
                    }}
                  />
                ) : (
                  <ObligacionesFiscalesTable
                    rows={obligacionesFiscalesRows}
                    loading={configLoading}
                    disabled={configLoading || importPdfBusy || updating}
                    onEditRow={idx => {
                      const row = obligacionesFiscalesRows[idx];
                      if (!row) return;
                      setMasInfoObligacionEditIndex(idx);
                      setObligacionDraft({
                        descripcionObligacion: str(row.descripcionObligacion),
                        descripcionVencimiento: str(row.descripcionVencimiento),
                        fechaInicio: vsellosToDateInput(row.fechaInicio),
                      });
                      setMasInfoObligacionUi('form');
                    }}
                    onRemoveRow={idx =>
                      setObligacionesFiscalesRows(prev => prev.filter((_, i) => i !== idx))
                    }
                  />
                )}
              </TabPanel>
              <TabPanel id="archivos-sat" label="Arch. SAT">
                {masInfoArchivoSatUi === 'form' ? (
                  <MasInfoArchivoAltaForm
                    titulo="Alta de archivo SAT"
                    idPrefix="sat"
                    disabled={masInfoFormBusy}
                    draft={archivoSatDraft}
                    setDraft={setArchivoSatDraft}
                    tipoCatalogSelect={{
                      label: 'Tipo Archivo SAT',
                      options: archivoSatTiposCatalog,
                      loading: archivoSatTiposLoading,
                      error: archivoSatTiposError,
                    }}
                    onCancel={() => {
                      setMasInfoArchivoSatUi('list');
                      setArchivoSatDraft({ nombreArchivo: '', file: null, tipoId: '' });
                    }}
                    onAccept={() => {
                      const name = archivoSatDraft.nombreArchivo.trim();
                      if (!name) return;
                      const tipoIdStr = str(archivoSatDraft.tipoId).trim();
                      if (!tipoIdStr) return;
                      const tipoNum = Number(tipoIdStr);
                      if (!Number.isFinite(tipoNum)) return;
                      const tipoOpt = archivoSatTiposCatalog.find(t => t.id === tipoNum);
                      if (!tipoOpt) return;
                      const f = archivoSatDraft.file;
                      setEmpresaConfig(prev => {
                        if (!prev) return prev;
                        const row: Record<string, unknown> = {
                          id: null,
                          nombreArchivo: name,
                          archivoSAT: archivoTipoNestedFromCatalogOpt(tipoOpt),
                        };
                        if (f) row[EMPRESA_CONFIG_UPLOAD_FILE_KEY] = f;
                        return {
                          ...prev,
                          archivosSat: [
                            ...(Array.isArray(prev.archivosSat) ? prev.archivosSat : []),
                            row,
                          ],
                        };
                      });
                      setMasInfoArchivoSatUi('list');
                      setArchivoSatDraft({ nombreArchivo: '', file: null, tipoId: '' });
                    }}
                  />
                ) : (
                  <ArchivosEmpresaTable
                    rows={
                      empresaConfig && Array.isArray(empresaConfig.archivosSat)
                        ? empresaConfig.archivosSat
                        : []
                    }
                    loading={configLoading}
                    emptyLabel="Sin archivos SAT."
                    disabled={configLoading || importPdfBusy || updating}
                    grupoId={grupoId!}
                    empresaId={empresaId!}
                    onRemoveRow={idx =>
                      setEmpresaConfig(prev => {
                        if (!prev || !Array.isArray(prev.archivosSat)) return prev;
                        const next = prev.archivosSat.filter((_, i) => i !== idx);
                        return { ...prev, archivosSat: next };
                      })
                    }
                  />
                )}
              </TabPanel>
              <TabPanel id="archivos-sellos" label="Arch. Sellos">
                {masInfoArchivoSellosUi === 'form' ? (
                  <MasInfoArchivoAltaForm
                    titulo="Alta de archivo de sellos"
                    idPrefix="sellos"
                    disabled={masInfoFormBusy}
                    draft={archivoSellosDraft}
                    setDraft={setArchivoSellosDraft}
                    tipoCatalogSelect={{
                      label: 'Tipo Archivo Sellos',
                      options: archivoSellosTiposCatalog,
                      loading: archivoSellosTiposLoading,
                      error: archivoSellosTiposError,
                    }}
                    onCancel={() => {
                      setMasInfoArchivoSellosUi('list');
                      setArchivoSellosDraft({ nombreArchivo: '', file: null, tipoId: '' });
                    }}
                    onAccept={() => {
                      const name = archivoSellosDraft.nombreArchivo.trim();
                      if (!name) return;
                      const tipoIdStr = str(archivoSellosDraft.tipoId).trim();
                      if (!tipoIdStr) return;
                      const tipoNum = Number(tipoIdStr);
                      if (!Number.isFinite(tipoNum)) return;
                      const tipoOpt = archivoSellosTiposCatalog.find(t => t.id === tipoNum);
                      if (!tipoOpt) return;
                      const f = archivoSellosDraft.file;
                      setEmpresaConfig(prev => {
                        if (!prev) return prev;
                        const row: Record<string, unknown> = {
                          id: null,
                          nombreArchivo: name,
                          archivoSAT: archivoTipoNestedFromCatalogOpt(tipoOpt),
                        };
                        if (f) row[EMPRESA_CONFIG_UPLOAD_FILE_KEY] = f;
                        return {
                          ...prev,
                          archivosSellos: [
                            ...(Array.isArray(prev.archivosSellos) ? prev.archivosSellos : []),
                            row,
                          ],
                        };
                      });
                      setMasInfoArchivoSellosUi('list');
                      setArchivoSellosDraft({ nombreArchivo: '', file: null, tipoId: '' });
                    }}
                  />
                ) : (
                  <ArchivosEmpresaTable
                    rows={
                      empresaConfig && Array.isArray(empresaConfig.archivosSellos)
                        ? empresaConfig.archivosSellos
                        : []
                    }
                    loading={configLoading}
                    emptyLabel="Sin archivos de sellos."
                    tipoArchivoColumnTitle="Tipo Archivo Sellos"
                    disabled={configLoading || importPdfBusy || updating}
                    grupoId={grupoId!}
                    empresaId={empresaId!}
                    onRemoveRow={idx =>
                      setEmpresaConfig(prev => {
                        if (!prev || !Array.isArray(prev.archivosSellos)) return prev;
                        const next = prev.archivosSellos.filter((_, i) => i !== idx);
                        return { ...prev, archivosSellos: next };
                      })
                    }
                  />
                )}
              </TabPanel>
              <TabPanel id="quo-contabilita" label="QUO Contabilita">
                {configLoading ? (
                  <div style={placeholderPanelStyle}>Cargando…</div>
                ) : masInfoQuoContabilitaUi === 'form' ? (
                  <MasInfoQuoContabilitaForm
                    disabled={masInfoFormBusy}
                    draft={quoContabilitaDraft}
                    setDraft={setQuoContabilitaDraft}
                    grupoId={grupoId}
                    empresaId={empresaIdForQuoKeyFetch}
                    onContrasenaEdited={() => {
                      quoContabilitaContrasenaDirtyRef.current = true;
                    }}
                    onCancel={() => {
                      setMasInfoQuoContabilitaUi('list');
                      setQuoContabilitaDraft({ quoId: '', usuario: '', contrasena: '' });
                    }}
                    onAccept={() => {
                      const pwdMod = quoContabilitaContrasenaDirtyRef.current;
                      setEmpresaConfig(prev =>
                        prev ? mergeQuoContabilitaIntoEmpresaConfig(prev, quoContabilitaDraft, pwdMod) : prev,
                      );
                      setQuoContabilitaPwdOnServer(prevPwd => {
                        if (!pwdMod) return prevPwd;
                        return quoContabilitaDraft.contrasena.trim() !== '';
                      });
                      setMasInfoQuoContabilitaUi('list');
                      setQuoContabilitaDraft({ quoId: '', usuario: '', contrasena: '' });
                    }}
                  />
                ) : (
                  <QuoContabilitaResumen
                    config={empresaConfig}
                    passwordKnownOnServer={quoContabilitaPwdOnServer}
                    grupoId={grupoId}
                    empresaId={empresaIdForQuoKeyFetch}
                  />
                )}
              </TabPanel>
              <TabPanel id="quo-facturita" label="QUO Facturita">
                {configLoading ? (
                  <div style={placeholderPanelStyle}>Cargando…</div>
                ) : masInfoQuoFacturitaUi === 'form' ? (
                  <MasInfoQuoFacturitaForm
                    disabled={masInfoFormBusy}
                    draft={quoFacturitaDraft}
                    setDraft={setQuoFacturitaDraft}
                    grupoId={grupoId}
                    empresaId={empresaIdForQuoKeyFetch}
                    onContrasenaEdited={() => {
                      quoFacturitaContrasenaDirtyRef.current = true;
                    }}
                    onCancel={() => {
                      setMasInfoQuoFacturitaUi('list');
                      setQuoFacturitaDraft({ usuario: '', contrasena: '' });
                    }}
                    onAccept={() => {
                      const pwdMod = quoFacturitaContrasenaDirtyRef.current;
                      setEmpresaConfig(prev =>
                        prev ? mergeQuoFacturitaIntoEmpresaConfig(prev, quoFacturitaDraft, pwdMod) : prev,
                      );
                      setQuoFacturitaPwdOnServer(prevPwd => {
                        if (!pwdMod) return prevPwd;
                        return quoFacturitaDraft.contrasena.trim() !== '';
                      });
                      setMasInfoQuoFacturitaUi('list');
                      setQuoFacturitaDraft({ usuario: '', contrasena: '' });
                    }}
                  />
                ) : (
                  <QuoFacturitaResumen
                    config={empresaConfig}
                    passwordKnownOnServer={quoFacturitaPwdOnServer}
                    grupoId={grupoId}
                    empresaId={empresaIdForQuoKeyFetch}
                  />
                )}
              </TabPanel>
            </TabPanelContainer>
            </div>
          </div>

          <div className="profase-form-actions" style={actionsStyle}>
            <button
              type="button"
              className="profase-form-btn-secondary"
              onClick={onClose}
              disabled={updating || configLoading || importPdfBusy}
            >
              <FaArrowLeft style={{ marginRight: 6 }} />
              Cancelar
            </button>
            {!masInfoInFormMode && renderMasInfoContextualButton()}
            <button
              type="button"
              className="profase-form-btn-secondary"
              onClick={handlePdfImportClick}
              disabled={updating || configLoading || importPdfBusy || !empresaConfig?.info}
            >
              <FaFileImport style={{ marginRight: 6 }} />
              {importPdfBusy ? 'Importando…' : 'Importar'}
            </button>
            <button
              type="submit"
              className="profase-form-btn-primary"
              style={btnPrimary(updating || configLoading || importPdfBusy)}
              disabled={updating || configLoading || importPdfBusy || !empresaConfig?.info}
            >
              <FaSave style={{ marginRight: 6 }} />
              {updating ? 'Guardando…' : 'Guardar cambios'}
            </button>
          </div>
          <input
            ref={pdfInputRef}
            type="file"
            accept="application/pdf,.pdf"
            style={{ display: 'none' }}
            aria-hidden
            tabIndex={-1}
            onChange={handlePdfFileChange}
          />
        </form>
    </ProfaseModal>
  );
}
